import { delegateMatchesLive, forwardingMatchesLive, pickupGroupMatchesLive, unansweredMatchesLive } from './calling-settings';
import {
  AA_CALLABLE_ENTITY_KINDS,
  AA_DIRECTORY_SEARCH_METHODS,
  AA_DTMF_RESPONSES,
  AA_MENU_OPTION_ACTIONS,
  AA_SCHEDULE_TYPES,
  CALL_QUEUE_NO_AGENT_ACTIONS,
  CALL_QUEUE_OVERFLOW_ACTIONS,
  CALL_QUEUE_ROUTING_METHODS,
  CALL_QUEUE_TIMEOUT_ACTIONS,
  GREETING_TYPES,
  POLICY_KIND_TO_TENANT_TYPE,
  POLICY_KINDS,
  RESOURCE_ACCOUNT_APPLICATION_IDS,
} from './domain';
import type { LiveAutoAttendantStructured } from './aa-live-parse';
import { accessWarnings, resolveUpnIds, sameIds, type UserNameExtension } from './voice-app-access';

/**
 * What Discovery's live-tenant snapshot (tenant_users) knows about a UPN -
 * the same data BuildValidationService compares against for Design & Build's
 * amber "pending change" badge (apps/api/src/modules/build/
 * build-validation.service.ts), reused here so a deployment only issues a
 * cmdlet when it would actually change something. `undefined` means no live
 * row was found for this UPN (e.g. a brand-new user not yet in the tenant) -
 * callers fall back to always emitting, since there's nothing to diff
 * against.
 */
export interface LiveIdentityState {
  enterpriseVoiceEnabled: boolean;
  lineUri: string | null;
  /** keyed by TenantPolicyType (e.g. 'OnlineVoiceRoutingPolicy') - the raw tenant_users.policies shape. */
  policies: Record<string, string | null>;
  /** tenant_users.entra_id - the real Entra/Azure AD object GUID, needed by cmdlets that take -Users/-Identities as GUIDs (e.g. Set-CsCallQueue), not UPNs. NOT tenant_users.object_id, which is our own internal tenant_objects.id row reference - a bug fixed this session (confirmed against OVP012's real live Call Queue Agents, whose ObjectId matched entra_id, never object_id). */
  objectId?: string;
  /**
   * From a targeted Get-CsOnlineVoicemailUserSettings check (Design &
   * Build's "Validate against tenant"), not the full-tenant sweep -
   * Get-CsOnlineVoicemailUserSettings has no bulk/wildcard form (confirmed
   * against Microsoft Learn: -Identity is mandatory, single value), so it
   * can't join Get-CsOnlineUser's tenant-wide sweep. `undefined` means this
   * UPN has never had a targeted check run - planIdentityRow falls back to
   * always emitting, same as an unmatched `live` elsewhere in this file.
   */
  voicemailEnabled?: boolean;
  voicemailPromptLanguage?: string | null;
  /**
   * Get-CsUserCallingSettings, read by the sync for voice-enabled users. `undefined` = never read for this user, so planIdentityRow
   * falls back to always emitting a designed call forwarding / call group / delegate setting.
   */
  callingSettings?: import('./calling-settings').LiveCallingSettings;
}

/** Last 10 significant digits, for loose number matching (same rule Site Data and Design & Build use). */
function numKey(v: string | null | undefined): string {
  return String(v ?? '').replace(/\D/g, '').slice(-10);
}

/**
 * One local, non-mutating object-constructor line needed before a compound
 * cmdlet can run - e.g. Auto Attendant deployment's real construction chain
 * (New-CsAutoAttendantCallableEntity -> ...Prompt -> ...MenuOption ->
 * ...Menu -> ...CallFlow -> New-CsOnlineSchedule -> ...CallHandlingAssociation)
 * before the one call that actually mutates the tenant
 * (New-CsAutoAttendant). Assigned to a PowerShell variable so later steps -
 * including the CmdletInvocation's own `parameters` - can reference it via
 * a `VarRef` instead of a literal value. Never audited/recorded on its own;
 * see renderCommand and handleDeploymentRun in apps/worker/src/main.ts,
 * which treats a CmdletInvocation with a `preamble` as a single change.
 */
export interface PreambleCallStep {
  kind?: 'call';
  /** PowerShell variable name (no leading $) this step's result is assigned to. */
  assignTo: string;
  cmdlet: string;
  parameters: Record<string, unknown>;
}

/**
 * `$target.property = value` - not a cmdlet call. Set-CsAutoAttendant has no
 * -Identity/-Name/-LanguageId/... parameters of its own (confirmed against
 * Microsoft Learn - its entire parameter surface is `-Instance`/`-Tenant`);
 * the only supported pattern is Get-CsAutoAttendant -Identity ... into a
 * variable, set each property on that object, then
 * Set-CsAutoAttendant -Instance $that. These steps are how
 * planAutoAttendantRow renders the "set each property" half of that.
 */
export interface PreambleAssignStep {
  kind: 'assign';
  /** PowerShell variable name (no leading $) whose property is being set. */
  target: string;
  property: string;
  value: unknown;
}

export type PreambleStep = PreambleCallStep | PreambleAssignStep;

/**
 * Sentinel: reference an earlier PreambleStep's result by variable name,
 * instead of a literal value. `prop` renders a property access on it
 * (`$name.Id`) - needed for New-CsAutoAttendantCallHandlingAssociation's
 * -ScheduleId/-CallFlowId, which take the *string* Id a locally-constructed
 * New-CsOnlineSchedule/New-CsAutoAttendantCallFlow object generates, not the
 * object itself.
 */
export interface VarRef {
  $var: string;
  prop?: string;
}

/**
 * "Global" isn't a per-user policy you can grant by name - it's what a user
 * has when there's no per-user assignment. Teams reports it as no value
 * (Get-CsOnlineUser's policy field is empty), and it's granted by unassigning:
 * Grant-Cs*Policy -PolicyName $null (Microsoft Learn: "To unassign a per-user
 * policy, set the PolicyName to a null value ($null)").
 */
export function isGlobalPolicyName(name: string | null | undefined): boolean {
  return !!name && /^global$/i.test(name.trim());
}

/** Whether a user's live policy already matches the design - with "Global" matching no assignment at all. */
export function policyMatchesLive(target: string, live: string | null | undefined): boolean {
  if (isGlobalPolicyName(target)) return live == null || live === '' || isGlobalPolicyName(live);
  return live === target;
}

/** The -PolicyName value to grant a designed policy: the name, or $null for Global. */
function grantPolicyName(target: string): string | VarRef {
  return isGlobalPolicyName(target) ? { $var: 'null' } : target;
}

function isVarRef(v: unknown): v is VarRef {
  return typeof v === 'object' && v !== null && typeof (v as VarRef).$var === 'string';
}

/**
 * Renders as a literal empty PowerShell array, `@()`. renderValue drops an
 * ordinary empty array (an optional parameter left blank), but a parameter that
 * is both mandatory and AllowEmptyCollection - Set-CsUserCallingSettings
 * -CallGroupTargets, which is how a call group is cleared - needs the explicit
 * empty list: left out, PowerShell prompts for it, and in the worker's piped
 * session that prompt waits for the next line of input until the command times
 * out. Only this one literal is ever rendered verbatim, never arbitrary text.
 */
export interface PsEmptyArray {
  $ps: '@()';
}
export const PS_EMPTY_ARRAY: PsEmptyArray = { $ps: '@()' };
function isPsEmptyArray(v: unknown): v is PsEmptyArray {
  return typeof v === 'object' && v !== null && (v as PsEmptyArray).$ps === '@()';
}

/**
 * The `cmdlet` of a deployment_changes row that records a user or common area phone left out of a run
 * (or stopped part way) instead of a command sent to Teams. Its `message` says why.
 */
export const NOT_DEPLOYED_CMDLET = '(not deployed)';

export interface CmdletInvocation {
  cmdlet: string;
  parameters: Record<string, unknown>;
  objectType: string;
  objectId?: string;
  /**
   * True for a cmdlet that must never run live - e.g. New-CsOnlineApplicationInstance,
   * which always needs a manual licensing step (a role a Teams Administrator
   * doesn't have) before anything downstream can succeed. A deferred call is
   * always rendered to the exported script and recorded as 'whatif', in both
   * dry_run and execute mode - see handleDeploymentRun in apps/worker/src/main.ts.
   */
  deferred?: boolean;
  /**
   * Local object-construction lines to run first - see PreambleStep.
   * `renderCommand` flattens these into one multi-line string for display
   * (What-If preview, exported PS1 script), but PwshTeamsExecutor.invoke
   * executes each step as its own separate round-trip to the pwsh session,
   * then the final call as one more - so a hang or error pins to a single
   * named step instead of an opaque combined script. Still exactly one
   * deployment_changes row per CmdletInvocation regardless of preamble size.
   */
  preamble?: PreambleStep[];
}

/**
 * Every character PowerShell treats as a single-quote delimiter: the straight
 * apostrophe plus the smart/typographic single quotes (U+2018 ‘, U+2019 ’,
 * U+201A ‚, U+201B ‛). Microsoft Learn (about_Quoting_Rules) says PowerShell
 * reads smart quotes as ordinary quotes, and the parser was confirmed to end a
 * single-quoted literal at each of these four.
 */
const PS_SINGLE_QUOTE_CHARS = /['\u2018\u2019\u201A\u201B]/g;

/**
 * PowerShell single-quoted string literal. Single quotes are the only safe
 * choice here: PS double-quoted strings interpolate `$variables` and treat
 * backslash as a plain character (not an escape), so `JSON.stringify` output
 * (which escapes `"` as `\"`) does NOT close cleanly inside one - a value
 * containing a `"` breaks out of the literal and the remainder is parsed as
 * live PowerShell. Inside a single-quoted literal nothing is special except
 * the single-quote characters, and each one is escaped by doubling it (Learn:
 * `'don''t'`, and `'‘‘smart’’'` for the smart forms). Doubling every one of the
 * four quote characters is what keeps a value like `Sales’ Queue` from ending
 * the literal early - previously only the straight `'` was doubled, so a
 * curly apostrophe broke the script, and anything after it ran as PowerShell.
 */
export function psQuote(v: string): string {
  return `'${v.replace(PS_SINGLE_QUOTE_CHARS, (q) => q + q)}'`;
}

/** Renders a VarRef as `$name` or, with `prop` set, `$name.Prop` (no quoting either way - it's a variable/property reference, not a literal). */
function renderVarRef(v: VarRef): string {
  return v.prop ? `$${v.$var}.${v.prop}` : `$${v.$var}`;
}

/** Render one -Param value; a VarRef renders per renderVarRef. */
function renderValue(v: unknown): string | null {
  if (v === undefined || v === null || v === '') return null;
  if (isVarRef(v)) return renderVarRef(v);
  if (isPsEmptyArray(v)) return '@()';
  if (Array.isArray(v)) {
    if (v.length === 0) return null;
    return `@(${v.map((item) => (isVarRef(item) ? renderVarRef(item) : psQuote(String(item)))).join(', ')})`;
  }
  if (typeof v === 'boolean') return `$${v}`;
  if (typeof v === 'number') return String(v);
  return psQuote(String(v));
}

/** Render one `Cmdlet -Param value ...` statement, with no assignment/wrapping. */
export function renderStatement(cmdlet: string, parameters: Record<string, unknown>): string {
  const parts = [cmdlet];
  for (const [k, v] of Object.entries(parameters)) {
    const rendered = renderValue(v);
    if (rendered === null) continue;
    // A boolean must bind with colon syntax (-Param:$true), never a bare
    // space (-Param $true) - PowerShell only auto-consumes the next token
    // for a switch parameter when it's `:`-joined; written with a space,
    // $true/$false is parsed as a *positional* argument instead, and fails
    // with "A positional parameter cannot be found that accepts argument
    // 'True'" the moment the cmdlet has no positional slot free - confirmed
    // live on New-CsOnlineSchedule's -WeeklyRecurrentSchedule/-Complement.
    // Colon syntax is valid for every switch AND every plain [bool]
    // parameter, so it's safe to use unconditionally here.
    parts.push(typeof v === 'boolean' ? `-${k}:${rendered}` : `-${k} ${rendered}`);
  }
  return parts.join(' ');
}

/**
 * Render exactly one PreambleStep as its own standalone statement - either
 * `$var = Cmdlet ...` (call) or `$target.property = value` (assign). Used
 * both by renderCommand (the flattened preview/export text) and by
 * PwshTeamsExecutor.invoke, which now runs each step as its own separate
 * round-trip to the pwsh session instead of bundling every step into one
 * script - see that file for why (a hung step used to be untraceable inside
 * an opaque multi-line blob).
 */
export function renderPreambleStep(step: PreambleStep): string {
  return step.kind === 'assign'
    ? `$${step.target}.${step.property} = ${renderValue(step.value) ?? '$null'}`
    : `$${step.assignTo} = ${renderStatement(step.cmdlet, step.parameters)}`;
}

/**
 * Render a cmdlet + params as PowerShell (What-If output and the exported
 * PS1 script). A `preamble` renders as one construction line per step
 * before it, each usable by name (via VarRef) in any later step or in this
 * call's own `parameters`. This flattened text is for *display* only now -
 * PwshTeamsExecutor.invoke executes each line as its own round-trip rather
 * than sending this whole string at once.
 */
export function renderCommand(call: CmdletInvocation): string {
  const lines = (call.preamble ?? []).map(renderPreambleStep);
  lines.push(renderStatement(call.cmdlet, call.parameters));
  return lines.join('\n');
}

/**
 * The exported .ps1 for a dry run or a read-only tenant, the only way a change
 * reaches such a tenant. The live path runs every step with -ErrorAction Stop
 * and abandons the object at its first failure; bare commands in a script keep
 * going after an error, so a failed New-CsAutoAttendantCallFlow leaves $flow1
 * empty and the next command builds from it. Stopping at the first error is the
 * safe equivalent for a script run by hand (Microsoft Learn's own examples use
 * -ErrorAction Stop for the same reason), and Disconnect still runs.
 */
export function renderExportScript(lines: string[], opts: { deploymentId: string }): string {
  return [
    '#Requires -Modules MicrosoftTeams',
    `# Voxshift deployment ${opts.deploymentId} - review each command before running.`,
    "$ErrorActionPreference = 'Stop'",
    'Connect-MicrosoftTeams',
    'try {',
    ...lines,
    '} finally {',
    '  Disconnect-MicrosoftTeams',
    '}',
  ].join('\n');
}

/** Comment line placed before a deferred call in the exported script (see CmdletInvocation.deferred). */
export const DEFERRED_SCRIPT_NOTE =
  '# Deferred: needs a manual licensing step (not possible with a Teams Administrator role) before the commands that depend on it can succeed.';

/** Mirrors dto.ts's callForwardingSchema exactly - see that file for the field-by-field cmdlet mapping. */
export interface CallForwardingSettings {
  forwarding?: {
    enabled: boolean;
    type?: 'Immediate' | 'Simultaneous';
    targetType?: 'Voicemail' | 'SingleTarget' | 'MyDelegates' | 'Group';
    target?: string | null;
  };
  unanswered?: {
    enabled: boolean;
    delaySeconds?: number;
    targetType?: 'Voicemail' | 'SingleTarget' | 'MyDelegates' | 'Group';
    target?: string | null;
  };
  busyOnBusy?: 'PlayBusySignal' | 'RedirectAsUnansweredCall' | 'RingUser';
}

/** Mirrors dto.ts's pickupGroupSchema - Set-CsUserCallingSettings' CallGroup settings group. */
export interface PickupGroupSettings {
  order: 'Simultaneous' | 'InOrder';
  targets: string[];
}

/** Mirrors dto.ts's delegateSchema - New-CsUserCallingDelegate's own parameters, one entry per delegate. */
export interface CallDelegate {
  delegateUpn: string;
  makeCalls: boolean;
  receiveCalls: boolean;
  manageSettings: boolean;
  pickUpHeldCalls: boolean;
  joinActiveCalls: boolean;
}

export interface BuildIdentityRow {
  id: string;
  upn: string;
  e164: string | null;
  number_type: string | null;
  revoke_ev: boolean;
  policies: Record<string, string | null> | null;
  voicemail: { enabled?: boolean | null; language?: string | null } | null;
  call_forwarding: CallForwardingSettings | null;
  pickup_group: PickupGroupSettings | null;
  delegates: CallDelegate[] | null;
}

/** hh:mm:ss format Set-CsUserCallingSettings' -UnansweredDelay expects. */
function toHms(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/**
 * Flags a build_users/build_caps row whose phone number won't actually get
 * assigned: planIdentityRow's Set-CsPhoneNumberAssignment guard (below)
 * requires `number_type` too, and a row can easily have `e164` set with
 * `number_type` still null (e.g. populated from Site Data on a site with no
 * PSTN/licensing model chosen yet) - that command, and the Enterprise Voice
 * enablement it carries, then just silently never appears in the plan.
 */
export function identityRowWarnings(row: Pick<BuildIdentityRow, 'e164' | 'number_type' | 'policies'>): string[] {
  if (row.policies?.shared_calling_policy && row.e164) {
    // Per Microsoft's Shared Calling setup guide, a Shared Calling user
    // shouldn't be assigned a phone number at all - planIdentityRow enables
    // Enterprise Voice directly for these rows and never emits
    // Set-CsPhoneNumberAssignment with a number, so this row's number is
    // just being silently ignored rather than deployed wrong - flag it so
    // it's not mistaken for a working assignment.
    return [
      "Shared Calling policy is set but this row also has a phone number - Shared Calling users shouldn't have one assigned. The number on this row will be ignored by deployment.",
    ];
  }
  return row.e164 && !row.number_type
    ? ['Phone number set but no Number type - Set-CsPhoneNumberAssignment (and Enterprise Voice) will be skipped until Number type is set.']
    : [];
}

/**
 * Turn a build_users/build_caps row into the ordered list of cmdlets the
 * deployment engine would run. Mirrors the order in the real
 * Teams-Migration-Build .ps1: number assignment first, then policy grants.
 * Users and CAPs plan identically - only the object type tag differs, matching
 * `deployment_changes.object_type` ('user' | 'cap').
 */
export function planIdentityRow(
  row: BuildIdentityRow,
  objectType: 'user' | 'cap',
  live?: LiveIdentityState,
): CmdletInvocation[] {
  const calls: CmdletInvocation[] = [];
  const identity = row.upn;

  if (row.revoke_ev) {
    // Skip if live already shows EV off - nothing left to revoke.
    if (!live || live.enterpriseVoiceEnabled) {
      calls.push({
        cmdlet: 'Remove-CsPhoneNumberAssignment',
        parameters: { Identity: identity, RemoveAll: true },
        objectType,
        objectId: row.id,
      });
    }
    return calls;
  }

  if (row.policies?.shared_calling_policy) {
    // Shared Calling users don't get a phone number - Set-CsPhoneNumberAssignment's
    // separate "Attribute" parameter set enables Enterprise Voice on its own
    // (see docs/DEPLOYMENT.md and Microsoft's Shared Calling setup guide).
    // The Grant-CsTeamsSharedCallingRoutingPolicy call below (from POLICY_KINDS)
    // is what actually gives them calling via the resource account.
    if (!live || !live.enterpriseVoiceEnabled) {
      calls.push({
        cmdlet: 'Set-CsPhoneNumberAssignment',
        parameters: { Identity: identity, EnterpriseVoiceEnabled: true },
        objectType,
        objectId: row.id,
      });
    }
  } else if (row.e164 && row.number_type && (!live || numKey(live.lineUri) !== numKey(row.e164))) {
    // Skip if live already has this number - can only compare the number
    // itself (normalized), not the assignment type (DirectRouting/CallingPlan/
    // etc): Discovery doesn't track live number type per-assignment.
    calls.push({
      cmdlet: 'Set-CsPhoneNumberAssignment',
      parameters: {
        Identity: identity,
        PhoneNumber: row.e164,
        PhoneNumberType: row.number_type,
      },
      objectType,
      objectId: row.id,
    });
  }

  for (const kind of POLICY_KINDS) {
    const value = row.policies?.[kind.key];
    if (!value) continue;
    // Every kind maps to a live TenantPolicyType (POLICY_KIND_TO_TENANT_TYPE);
    // the guard is just defensive for any future kind added without one.
    const tenantType = POLICY_KIND_TO_TENANT_TYPE[kind.key];
    if (tenantType && live && policyMatchesLive(value, live.policies[tenantType])) continue;
    calls.push({
      cmdlet: kind.cmdlet,
      parameters: { Identity: identity, PolicyName: grantPolicyName(value) },
      objectType,
      objectId: row.id,
    });
  }

  // Voicemail on/off + language are settings we set directly in Teams
  // (Set-CsOnlineVoicemailUserSettings), not a policy grant - separate from
  // voicemail_policy in POLICY_KINDS above, which governs a different set of
  // tenant-defined behaviours. `row.voicemail.enabled` undefined/null means
  // "not designed yet" and is left alone; explicitly true/false is a real
  // target either way. Diffed against live.voicemailEnabled/
  // voicemailPromptLanguage when available (from a targeted
  // Get-CsOnlineVoicemailUserSettings check - see LiveIdentityState); if that
  // UPN has never had a targeted check run, `live.voicemailEnabled` is
  // undefined and this falls back to always emitting, same as every other
  // live-optional check in this function.
  if (row.voicemail?.enabled != null) {
    const matches =
      live?.voicemailEnabled === row.voicemail.enabled &&
      (!row.voicemail.enabled || !row.voicemail.language || live.voicemailPromptLanguage === row.voicemail.language);
    if (!matches) {
      calls.push({
        cmdlet: 'Set-CsOnlineVoicemailUserSettings',
        parameters: {
          Identity: identity,
          VoicemailEnabled: row.voicemail.enabled,
          ...(row.voicemail.enabled && row.voicemail.language ? { PromptLanguage: row.voicemail.language } : {}),
        },
        objectType,
        objectId: row.id,
      });
    }
  }

  // Call forwarding / unanswered / busy-on-busy / pickup group / delegates -
  // Set-CsUserCallingSettings and New-CsUserCallingDelegate, per
  // docs/DEPLOYMENT.md's "natural next increment". None of these are
  // diffable against live state yet (Discovery doesn't capture
  // Get-CsUserCallingSettings - see build-validation.service.ts), so - like
  // voicemail above - they always re-issue whenever a target is designed.
  const liveCalling = live?.callingSettings;
  if (row.call_forwarding?.forwarding && !(liveCalling && forwardingMatchesLive(row.call_forwarding.forwarding, liveCalling.callForwarding.forwarding))) {
    const f = row.call_forwarding.forwarding;
    calls.push({
      cmdlet: 'Set-CsUserCallingSettings',
      parameters: {
        Identity: identity,
        IsForwardingEnabled: f.enabled,
        ...(f.enabled ? { ForwardingType: f.type, ForwardingTargetType: f.targetType, ForwardingTarget: f.target } : {}),
      },
      objectType,
      objectId: row.id,
    });
  }
  if (row.call_forwarding?.unanswered && !(liveCalling && unansweredMatchesLive(row.call_forwarding.unanswered, liveCalling.callForwarding.unanswered))) {
    const u = row.call_forwarding.unanswered;
    calls.push({
      cmdlet: 'Set-CsUserCallingSettings',
      parameters: {
        Identity: identity,
        IsUnansweredEnabled: u.enabled,
        ...(u.enabled
          ? {
              UnansweredDelay: toHms(u.delaySeconds ?? 20),
              UnansweredTargetType: u.targetType,
              UnansweredTarget: u.target,
            }
          : {}),
      },
      objectType,
      objectId: row.id,
    });
  }
  if (row.call_forwarding?.busyOnBusy && !(liveCalling && liveCalling.callForwarding.busyOnBusy === row.call_forwarding.busyOnBusy)) {
    calls.push({
      cmdlet: 'Set-CsUserCallingSettings',
      parameters: { Identity: identity, BusyOnBusyOption: row.call_forwarding.busyOnBusy },
      objectType,
      objectId: row.id,
    });
  }
  // build_users/build_caps.pickup_group defaults to `{}` (NOT NULL, see the
  // 0001_init migration) for every never-designed row, so checking the
  // object's mere presence would fire this call for literally every row on
  // every deploy. `order` is only ever set by the settings dialog's own
  // save (BuildSiteWorkspace.tsx) - present means "explicitly designed",
  // absent means "still the untouched default" - so it's the real signal,
  // not `.targets.length` (which the dialog can legitimately save as empty
  // to clear a previously-saved group).
  if (row.pickup_group?.order && !(liveCalling && pickupGroupMatchesLive(row.pickup_group, liveCalling.pickupGroup))) {
    calls.push({
      cmdlet: 'Set-CsUserCallingSettings',
      parameters: {
        Identity: identity,
        CallGroupOrder: row.pickup_group.order,
        // Empty means "clear the group": it must still be sent, as an explicit @().
        CallGroupTargets: row.pickup_group.targets?.length ? row.pickup_group.targets : PS_EMPTY_ARRAY,
      },
      objectType,
      objectId: row.id,
    });
  }
  for (const d of Array.isArray(row.delegates) ? row.delegates : []) {
    // New-CsUserCallingDelegate -Delegate is mandatory; a blank one would render
    // without it and make PowerShell prompt (hanging the run), so skip it.
    if (!d.delegateUpn?.trim()) continue;
    if (liveCalling && delegateMatchesLive(d, liveCalling.delegates)) continue;
    calls.push({
      cmdlet: 'New-CsUserCallingDelegate',
      parameters: {
        Identity: identity,
        Delegate: d.delegateUpn,
        MakeCalls: d.makeCalls,
        ReceiveCalls: d.receiveCalls,
        ManageSettings: d.manageSettings,
        PickUpHeldCalls: d.pickUpHeldCalls,
        JoinActiveCalls: d.joinActiveCalls,
      },
      objectType,
      objectId: row.id,
    });
  }

  return calls;
}

export interface BuildResourceAccountRow {
  id: string;
  upn: string;
  display_name: string | null;
  kind: 'auto_attendant' | 'call_queue';
  location_id: string | null;
  phone_number: string | null;
  number_type: string | null;
  voice_routing_policy: string | null;
  application_id: string | null;
}

/** Same gap as identityRowWarnings, for build_resource_accounts. */
export function resourceAccountRowWarnings(row: Pick<BuildResourceAccountRow, 'phone_number' | 'number_type'>): string[] {
  return row.phone_number && !row.number_type
    ? ['Phone number set but no Number type - Set-CsPhoneNumberAssignment (and Enterprise Voice) will be skipped until Number type is set.']
    : [];
}

/**
 * Resource accounts are a genuine two-phase process (see docs/DEPLOYMENT.md):
 * New-CsOnlineApplicationInstance always needs a Phone System license applied
 * by a User/Global Admin before anything else can succeed - a role a Teams
 * Administrator doesn't have. So phase 1 is always `deferred: true` (rendered
 * to the exported script, never invoked live, in either dry_run or execute
 * mode). Only once the row has an `application_id` from a completed phase 1
 * does phase 2 (number + voice routing policy, live-capable) run.
 */
export function planResourceAccountRow(row: BuildResourceAccountRow, live?: LiveIdentityState): CmdletInvocation[] {
  if (!row.application_id) {
    return [
      {
        cmdlet: 'New-CsOnlineApplicationInstance',
        parameters: {
          UserPrincipalName: row.upn,
          ApplicationId: RESOURCE_ACCOUNT_APPLICATION_IDS[row.kind],
          DisplayName: row.display_name ?? row.upn,
        },
        objectType: 'resource_account',
        objectId: row.id,
        deferred: true,
      },
    ];
  }

  const calls: CmdletInvocation[] = [];
  // Same diffing as planIdentityRow - a resource account is a normal
  // Entra/Teams identity under its own UPN, so it appears in tenant_users
  // (Discovery's live snapshot) just like a regular user or CAP.
  if (row.phone_number && row.number_type && (!live || numKey(live.lineUri) !== numKey(row.phone_number))) {
    calls.push({
      cmdlet: 'Set-CsPhoneNumberAssignment',
      parameters: {
        Identity: row.upn,
        PhoneNumber: row.phone_number,
        PhoneNumberType: row.number_type,
        ...(row.location_id ? { LocationId: row.location_id } : {}),
      },
      objectType: 'resource_account',
      objectId: row.id,
    });
  }
  if (row.voice_routing_policy && (!live || !policyMatchesLive(row.voice_routing_policy, live.policies['OnlineVoiceRoutingPolicy']))) {
    calls.push({
      cmdlet: 'Grant-CsOnlineVoiceRoutingPolicy',
      parameters: { Identity: row.upn, PolicyName: grantPolicyName(row.voice_routing_policy) },
      objectType: 'resource_account',
      objectId: row.id,
    });
  }
  return calls;
}

/* ========================================================================
 * Shared Calling routing policies - New-/Set-CsTeamsSharedCallingRoutingPolicy.
 * Confirmed against Microsoft Learn 2026-09-23: -Identity (name),
 * -ResourceAccount (an existing resource account's Identity/ObjectId, used
 * as outbound/callback caller ID), -EmergencyNumbers (array of PSTN numbers
 * reserved for emergency callback), -Description. A Shared Calling *user*
 * is granted an existing policy by name (POLICY_KINDS' shared_calling_policy
 * -> Grant-CsTeamsSharedCallingRoutingPolicy, already handled by
 * planIdentityRow) - this section is the separate, missing piece: managing
 * the policy OBJECT itself.
 * ======================================================================== */

export interface BuildSharedCallingPolicyRow {
  id: string;
  name: string;
  /** build_resource_accounts.id - resolved to its live Entra Object ID via raObjectIds before planning, same map planCallQueueRow/planAutoAttendantRow already use for a linked resource account. */
  resource_account_id: string | null;
  emergency_numbers: string[];
  description: string | null;
}

/**
 * What Discovery's live-tenant snapshot (tenant_policies, policy_type
 * 'TeamsSharedCallingRoutingPolicy') knows about a policy, matched by
 * lowercased Identity/name - no separate live capture needed, Discovery's
 * existing generic TENANT_POLICY_TYPES sweep (Get-Cs<type> for every entry,
 * apps/worker/src/discovery/cmdlets.ts) already stores the full raw object
 * under tenant_policies.data.
 */
export interface SharedCallingPolicyLiveState {
  identity: string;
  resourceAccount?: string;
  emergencyNumbers?: string[];
  description?: string;
}

/** Missing resource account is the only "silently produces an undeployable row" gap here - Microsoft Learn documents -ResourceAccount as required. */
export function sharedCallingPolicyRowWarnings(row: Pick<BuildSharedCallingPolicyRow, 'resource_account_id'>, raObjectIds: Map<string, string>): string[] {
  if (!row.resource_account_id) {
    return ['No resource account set - New-CsTeamsSharedCallingRoutingPolicy requires one, so this policy will not deploy.'];
  }
  if (!raObjectIds.has(row.resource_account_id)) {
    return ['Linked resource account has no live Application Instance yet, so this policy cannot deploy until that resolves.'];
  }
  return [];
}

/**
 * New- when the policy doesn't exist live yet, Set- when it does and
 * something differs. Skips entirely (no calls) when the resource account
 * hasn't resolved live - same "don't guess, don't send a cmdlet certain to
 * fail" rule as every other row here.
 */
export function planSharedCallingPolicyRow(
  row: BuildSharedCallingPolicyRow,
  raObjectIds: Map<string, string>,
  live?: SharedCallingPolicyLiveState,
): CmdletInvocation[] {
  const resourceAccount = row.resource_account_id ? raObjectIds.get(row.resource_account_id) : undefined;
  if (!resourceAccount) return [];

  const sortedNumbers = [...row.emergency_numbers].sort();
  const parameters: Record<string, unknown> = {
    Identity: row.name,
    ResourceAccount: resourceAccount,
    ...(row.emergency_numbers.length ? { EmergencyNumbers: row.emergency_numbers } : {}),
    ...(row.description ? { Description: row.description } : {}),
  };

  if (!live) {
    return [{ cmdlet: 'New-CsTeamsSharedCallingRoutingPolicy', parameters, objectType: 'shared_calling_policy', objectId: row.id }];
  }
  const sortedLive = [...(live.emergencyNumbers ?? [])].sort();
  const changed =
    (live.resourceAccount ?? '').toLowerCase() !== resourceAccount.toLowerCase() ||
    JSON.stringify(sortedNumbers) !== JSON.stringify(sortedLive) ||
    (live.description ?? undefined) !== (row.description ?? undefined);
  if (!changed) return [];
  return [{ cmdlet: 'Set-CsTeamsSharedCallingRoutingPolicy', parameters, objectType: 'shared_calling_policy', objectId: row.id }];
}

type CallQueueRoutingMethod = (typeof CALL_QUEUE_ROUTING_METHODS)[number];

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * Confirmed against Microsoft Learn: Set-CsCallQueue's Overflow/Timeout/
 * NoAgentActionTarget ("must be set to a Guid or a telephone number with a
 * mandatory 'tel:' prefix") and New-CsAutoAttendantCallableEntity's
 * ExternalPstn -Identity both require a raw PSTN number to carry a 'tel:'
 * prefix - a bare GUID (person, or a nested AA/CQ resource account) or an
 * already-prefixed value passes through unchanged.
 */
export function normalizePstnTarget(target: string): string {
  const trimmed = target.trim();
  if (/^tel:/i.test(trimmed) || GUID_RE.test(trimmed)) return trimmed;
  return `tel:${trimmed}`;
}

/** Mirrors dto.ts's callQueueActionSchema - Set-CsCallQueue's Overflow/Timeout parameter groups. */
export interface CallQueueActionSettings {
  action?: string;
  threshold?: number;
  target?: string | null;
  /**
   * PowerShell only (not in Teams Admin Center): the call's priority, 1 (highest) to 5, when it is forwarded to another call
   * queue or auto attendant - Set-CsCallQueue -OverflowActionCallPriority / -TimeoutActionCallPriority / -NoAgentActionCallPriority.
   */
  priority?: number;
  /**
   * PowerShell only: what the caller hears (text to speech) before being forwarded to a person or sent to a person's voicemail -
   * -<Overflow|Timeout|NoAgent>Redirect<Person|Voicemail>TextToSpeechPrompt.
   */
  redirectPrompt?: string;
  /** PowerShell only: an uploaded audio prompt (voice_audio_files) played instead - -<X>Redirect<Person|Voicemail>AudioFilePrompt. */
  redirectAudio?: { fileId: string; filename: string };
}

/**
 * A redirect audio prompt not yet imported to Teams: the worker swaps this for the id Import-CsOnlineAudioFile returns, in the
 * same live run (see resolveAudioPlaceholders in the worker). Shown as-is in a What-If.
 */
export const AUDIO_PLACEHOLDER = /^<audio:([0-9a-f-]{36})>$/i;
export const audioPlaceholder = (fileId: string) => `<audio:${fileId}>`;

/**
 * The Set-CsCallQueue parameters for one exception's PowerShell-only extras (priority, redirect prompt or audio), for its action.
 * `audio` = voice_audio_files.id -> the Teams audio file id it was imported as (absent: not imported yet, so a placeholder).
 */
export function callQueueActionExtras(prefix: 'Overflow' | 'Timeout' | 'NoAgent', settings: CallQueueActionSettings | null | undefined, audio: Map<string, string> = new Map()): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (settings?.action === 'Forward' && settings.priority != null) out[`${prefix}ActionCallPriority`] = settings.priority;
  const who = settings?.action === 'Forward' ? 'Person' : settings?.action === 'Voicemail' ? 'Voicemail' : null;
  const prompt = settings?.redirectPrompt?.trim();
  if (who && prompt) out[`${prefix}Redirect${who}TextToSpeechPrompt`] = prompt;
  if (who && settings?.redirectAudio) out[`${prefix}Redirect${who}AudioFilePrompt`] = audio.get(settings.redirectAudio.fileId) ?? audioPlaceholder(settings.redirectAudio.fileId);
  return out;
}

/** The live counterparts of callQueueActionExtras, from Get-CsCallQueue's output, for the action the queue has live. */
export function liveCallQueueActionExtras(prefix: 'Overflow' | 'Timeout' | 'NoAgent', data: Record<string, unknown>, action: string | undefined): { priority?: number; redirectPrompt?: string; redirectAudioId?: string } {
  const priority = data[`${prefix}ActionCallPriority`];
  const who = action === 'Voicemail' ? 'Voicemail' : action === 'Forward' ? 'Person' : null;
  const prompt = who ? data[`${prefix}Redirect${who}TextToSpeechPrompt`] : undefined;
  const audio = who ? data[`${prefix}Redirect${who}AudioFilePrompt`] : undefined;
  return {
    ...(action === 'Forward' && typeof priority === 'number' ? { priority } : {}),
    ...(typeof prompt === 'string' && prompt.trim() ? { redirectPrompt: prompt.trim() } : {}),
    ...(typeof audio === 'string' && audio ? { redirectAudioId: audio } : {}),
  };
}

export interface BuildCallQueueRow {
  id: string;
  name: string;
  routing_method: CallQueueRoutingMethod;
  agent_alert_time: number;
  presence_based_routing: boolean;
  /** agent UPNs - resolved to Entra object GUIDs via agentObjectIds before planning, see planCallQueueRow. */
  agents: string[];
  overflow: CallQueueActionSettings | null;
  timeout: CallQueueActionSettings | null;
  /** Zero-agents-opted-in action - same shape as overflow/timeout, Set-CsCallQueue -NoAgentAction/-NoAgentActionTarget. */
  no_agent_action: CallQueueActionSettings | null;
  /** Set-CsCallQueue -NoAgentApplyTo. */
  no_agent_apply_to: 'AllCalls' | 'NewCalls' | null;
  language_id: string | null;
  /** build_resource_accounts.id array - resolved to Application Instance GUIDs via raObjectIds. */
  resource_accounts: string[];
  /** -AuthorizedUsers / -HideAuthorizedUsers as UPNs, and -SharedVoicemailTriageSettingsTemplateId (voice-app-access.ts). null = not managed. */
  authorized_users?: string[] | null;
  hide_authorized_users?: string[] | null;
  voicemail_triage_template_id?: string | null;
}

/**
 * What Discovery's live-tenant snapshot (tenant_objects, object_type
 * 'call_queue') knows about a queue matched by Name - undefined means the
 * queue doesn't exist live yet, so New-CsCallQueue is emitted instead of
 * Set-CsCallQueue. `identity` is the queue's own Identity GUID, needed to
 * target Set-CsCallQueue and to associate a resource account with it.
 */
export interface CallQueueLiveState {
  identity: string;
  routingMethod?: string;
  agentAlertTime?: number;
  presenceBasedRouting?: boolean;
  agentObjectIds?: string[];
  overflowAction?: string;
  overflowThreshold?: number;
  /** Unwrapped via extractLiveCallTargetId - a raw GUID or 'tel:' number, directly comparable to resolveActionTarget's own output. */
  overflowActionTarget?: string;
  timeoutAction?: string;
  timeoutThreshold?: number;
  timeoutActionTarget?: string;
  /** No live NoAgentThreshold exists - "no agents" fires purely on zero agents opted in, not a numeric threshold. */
  noAgentAction?: string;
  noAgentActionTarget?: string;
  noAgentApplyTo?: string;
  authorizedUsers?: string[];
  hideAuthorizedUsers?: string[];
  voicemailTriageTemplateId?: string;
  /** PowerShell-only extras per exception (liveCallQueueActionExtras). */
  overflowExtras?: { priority?: number; redirectPrompt?: string; redirectAudioId?: string };
  timeoutExtras?: { priority?: number; redirectPrompt?: string; redirectAudioId?: string };
  noAgentExtras?: { priority?: number; redirectPrompt?: string; redirectAudioId?: string };
  languageId?: string;
  /**
   * From the live tenant_objects call_queue record's ApplicationInstances -
   * confirmed live across several real tenants this session (populates with
   * real GUIDs when a resource account is directly linked, `[]` when none
   * is - same field AutoAttendantLiveState already uses reliably), despite
   * an earlier comment here claiming Get-CsCallQueue's output "doesn't
   * reliably expose" this. That was wrong; the diffing below relies on it.
   */
  applicationInstanceIds?: string[];
}

/**
 * Get-CsCallQueue's Overflow/Timeout/NoAgentActionTarget each serialize as
 * an object ({ Id: string, ... }), not a plain string - same shape
 * populateStructuredFromLive's own targetOf closure
 * (apps/api/.../build.service.ts) already unwraps for the Populate path.
 * resolveLiveCallQueueState (duplicated in api and worker) needs the same
 * unwrap so planCallQueueRow's live-diff can compare it directly against
 * resolveActionTarget's own GUID/'tel:' output.
 */
export function extractLiveCallTargetId(raw: unknown): string | undefined {
  const o = raw as Record<string, unknown> | null;
  return o && typeof o.Id === 'string' ? o.Id : undefined;
}

/**
 * Get-CsCallQueue serializes RoutingMethod/OverflowAction/TimeoutAction/
 * NoAgentAction as their underlying numeric enum value, not the string name
 * PowerShell displays - confirmed against OVP012's real live tenant_objects
 * data this session (e.g. `OverflowAction: 3`, not `"SharedVoicemail"`).
 * Callers building CallQueueLiveState from raw tenant_objects.data must
 * decode through this instead of a bare `typeof v === 'string'` check, or
 * every real tenant's live-diff silently sees `undefined` and never detects
 * "unchanged". The numeric codes line up with these arrays' own index order.
 */
export function decodeCallQueueEnum(v: unknown, values: readonly string[]): string | undefined {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && values[v] !== undefined) return values[v];
  return undefined;
}

/**
 * Every UPN a Call Queue's Forward action targets (Overflow/Timeout/
 * NoAgent) across a batch of rows - the Design & Build editor's target
 * field is a freeform UpnAutocomplete (it also accepts a raw phone number
 * or an existing GUID), so a person target is stored as their plain UPN.
 * planCallQueueRow/callQueueRowWarnings need each one resolved to its live
 * Entra Object ID first (Set-CsCallQueue's …ActionTarget takes a GUID or a
 * 'tel:' number, never a bare UPN), so the caller collects them up front
 * the same way it already does for agent UPNs and
 * collectAutoAttendantUserUpns does for Auto Attendant 'user'-kind targets,
 * before calling resolveLiveIdentityState. Only 'Forward' is collected -
 * SharedVoicemail's target is an M365 group GUID, a different identity
 * space entirely, and the other actions don't use target at all.
 */
export function collectCallQueueTargetUpns(
  rows: Pick<BuildCallQueueRow, 'overflow' | 'timeout' | 'no_agent_action' | 'authorized_users' | 'hide_authorized_users'>[],
): string[] {
  const out: string[] = [];
  const add = (s: CallQueueActionSettings | null | undefined) => {
    if ((s?.action === 'Forward' || s?.action === 'Voicemail') && s.target) out.push(s.target);
  };
  for (const row of rows) {
    add(row.overflow);
    add(row.timeout);
    add(row.no_agent_action);
    // authorised users resolve the same way (UPN -> Entra object id)
    out.push(...(row.authorized_users ?? []), ...(row.hide_authorized_users ?? []));
  }
  return out;
}

/**
 * Set-CsCallQueue's …ActionTarget for one Overflow/Timeout/NoAgent action,
 * per Microsoft Learn: Forward = a person/voice-app GUID or a 'tel:' number;
 * Voicemail ("Voicemail (personal)") = the person whose mailbox receives it;
 * SharedVoicemail = an M365 group/DL GUID. Every other action takes no
 * target, so none is sent even if a stale one is still stored. Undefined =
 * no usable target (callQueueRowWarnings explains which).
 */
function resolveCallQueueActionTarget(settings: CallQueueActionSettings | null | undefined, userObjectIds: Map<string, string>): string | undefined {
  const target = settings?.target?.trim();
  if (!target) return undefined;
  switch (settings?.action) {
    case 'Forward':
      return userObjectIds.get(target.toLowerCase()) ?? normalizePstnTarget(target);
    case 'Voicemail':
      return userObjectIds.get(target.toLowerCase()) ?? (GUID_RE.test(target) ? target : undefined);
    case 'SharedVoicemail':
      return GUID_RE.test(target) ? target : undefined;
    default:
      return undefined;
  }
}

/** Teams rounds -TimeoutThreshold to the nearest 15 seconds (Microsoft Learn: 47 -> 45); sending the rounded value keeps the live diff from seeing 47 vs 45 as a change forever. */
function roundTimeoutThreshold(t: number | undefined): number | undefined {
  return t == null ? undefined : Math.min(2700, Math.max(0, Math.round(t / 15) * 15));
}

function sameTarget(a: string | undefined, b: string | undefined): boolean {
  return (a ?? '').toLowerCase() === (b ?? '').toLowerCase();
}

/**
 * Flags agent UPNs / linked resource accounts that don't resolve to a live
 * Entra object id - planCallQueueRow silently skips these (a typo'd UPN or a
 * resource account not yet licensed shouldn't block the rest of the queue's
 * config from deploying), so this is the only place that gap is visible.
 * Also flags a SharedVoicemail action with no language_id - planCallQueueRow
 * (see needsLanguage below) silently omits the LanguageId cmdlet parameter
 * in that case rather than guessing one, which Teams then rejects at deploy
 * time; surfacing it here means the gap is visible in the preview instead of
 * as a failed cmdlet.
 */
export function callQueueRowWarnings(
  row: Pick<BuildCallQueueRow, 'agents' | 'resource_accounts' | 'overflow' | 'timeout' | 'no_agent_action' | 'language_id' | 'authorized_users' | 'hide_authorized_users'>,
  agentObjectIds: Map<string, string>,
  raObjectIds: Map<string, string>,
): string[] {
  const warnings: string[] = [];
  const unresolvedAgents = row.agents.filter((upn) => !agentObjectIds.has(upn.toLowerCase()));
  if (unresolvedAgents.length) {
    warnings.push(
      `${unresolvedAgents.length} agent UPN(s) not found in the tenant's synced users, so they'll be skipped: ${unresolvedAgents.join(', ')}`,
    );
  }
  const unresolvedRas = row.resource_accounts.filter((id) => !raObjectIds.has(id));
  if (unresolvedRas.length) {
    warnings.push(
      `${unresolvedRas.length} linked resource account(s) have no live Application Instance yet (New-CsOnlineApplicationInstance not run), so this queue won't get a phone number until that's done.`,
    );
  }
  const needsLanguage =
    row.overflow?.action === 'SharedVoicemail' ||
    row.timeout?.action === 'SharedVoicemail' ||
    row.no_agent_action?.action === 'SharedVoicemail';
  if (needsLanguage && !row.language_id) {
    warnings.push('A SharedVoicemail action is configured but no language is set, so this queue will fail to deploy until one is chosen.');
  }
  // Each action's target rules - see resolveCallQueueActionTarget.
  const checkTarget = (settings: CallQueueActionSettings | null | undefined, where: string) => {
    const target = settings?.target?.trim();
    switch (settings?.action) {
      case 'Forward':
        if (!target) warnings.push(`${where} forwards calls but has no destination set, so it won't deploy correctly.`);
        else if (!/^tel:/i.test(target) && !GUID_RE.test(target) && !/^\+?[\d\s()-]+$/.test(target) && !agentObjectIds.has(target.toLowerCase())) {
          warnings.push(`${where} forwards to "${target}", which doesn't resolve to a live Entra identity yet, so it won't deploy correctly.`);
        }
        break;
      case 'Voicemail':
        if (!target) warnings.push(`${where} sends calls to personal voicemail but no person is set - choose whose voicemail receives them.`);
        else if (!GUID_RE.test(target) && !agentObjectIds.has(target.toLowerCase())) {
          warnings.push(`${where} sends calls to "${target}"'s voicemail, which doesn't resolve to a live Entra identity yet, so it won't deploy correctly.`);
        }
        break;
      case 'SharedVoicemail':
        if (!target || !GUID_RE.test(target)) warnings.push(`${where} sends calls to shared voicemail but no Microsoft 365 group is selected, so it won't deploy correctly.`);
        break;
    }
  };
  checkTarget(row.overflow, 'Overflow');
  checkTarget(row.timeout, 'Timeout');
  checkTarget(row.no_agent_action, 'No-agent');
  warnings.push(...accessWarnings(row.authorized_users, row.hide_authorized_users, agentObjectIds));
  return warnings;
}

/**
 * Turn a build_call_queues row into the ordered list of cmdlets: create/
 * update the queue itself (New-CsCallQueue / Set-CsCallQueue), then
 * associate any linked resource accounts so the queue actually has a phone
 * number (New-CsOnlineApplicationInstanceAssociation). Only the Call Queue
 * subset of Set-CsCallQueue's full parameter surface is implemented here -
 * see the Phase B plan for the full list of what's deliberately left out
 * (ConferenceMode, DistributionLists, compliance recording, etc).
 */
export function planCallQueueRow(
  row: BuildCallQueueRow,
  agentObjectIds: Map<string, string>,
  raObjectIds: Map<string, string>,
  live?: CallQueueLiveState,
  /** voice_audio_files.id -> Teams audio file id, for redirect audio prompts already imported (callQueueActionExtras). */
  audio: Map<string, string> = new Map(),
): CmdletInvocation[] {
  const calls: CmdletInvocation[] = [];
  const users = row.agents
    .map((upn) => agentObjectIds.get(upn.toLowerCase()))
    .filter((id): id is string => !!id);
  const needsLanguage =
    row.overflow?.action === 'SharedVoicemail' ||
    row.timeout?.action === 'SharedVoicemail' ||
    row.no_agent_action?.action === 'SharedVoicemail';

  // A Forward/Voicemail target picked via the Design & Build editor's
  // UpnAutocomplete is stored as the person's plain UPN, not their Entra
  // Object ID - resolved the same way -Users is (see
  // resolveCallQueueActionTarget for every action's rules).
  const overflowTarget = resolveCallQueueActionTarget(row.overflow, agentObjectIds);
  const timeoutTarget = resolveCallQueueActionTarget(row.timeout, agentObjectIds);
  const noAgentTarget = resolveCallQueueActionTarget(row.no_agent_action, agentObjectIds);
  const timeoutThreshold = roundTimeoutThreshold(row.timeout?.threshold);

  const parameters: Record<string, unknown> = {
    Name: row.name,
    RoutingMethod: row.routing_method,
    AgentAlertTime: row.agent_alert_time,
    PresenceBasedRouting: row.presence_based_routing,
    Users: users,
    ...(needsLanguage && row.language_id ? { LanguageId: row.language_id } : {}),
    ...(row.overflow?.action ? { OverflowAction: row.overflow.action } : {}),
    ...(row.overflow?.threshold != null ? { OverflowThreshold: row.overflow.threshold } : {}),
    ...(overflowTarget ? { OverflowActionTarget: overflowTarget } : {}),
    ...(row.timeout?.action ? { TimeoutAction: row.timeout.action } : {}),
    ...(timeoutThreshold != null ? { TimeoutThreshold: timeoutThreshold } : {}),
    ...(timeoutTarget ? { TimeoutActionTarget: timeoutTarget } : {}),
    ...(row.no_agent_action?.action ? { NoAgentAction: row.no_agent_action.action } : {}),
    ...(noAgentTarget ? { NoAgentActionTarget: noAgentTarget } : {}),
    ...(row.no_agent_apply_to ? { NoAgentApplyTo: row.no_agent_apply_to } : {}),
    ...callQueueActionExtras('Overflow', row.overflow, audio),
    ...callQueueActionExtras('Timeout', row.timeout, audio),
    ...callQueueActionExtras('NoAgent', row.no_agent_action, audio),
    ...(row.authorized_users != null ? { AuthorizedUsers: resolveUpnIds(row.authorized_users, agentObjectIds).length ? resolveUpnIds(row.authorized_users, agentObjectIds) : PS_EMPTY_ARRAY } : {}),
    ...(row.hide_authorized_users != null ? { HideAuthorizedUsers: resolveUpnIds(row.hide_authorized_users, agentObjectIds).length ? resolveUpnIds(row.hide_authorized_users, agentObjectIds) : PS_EMPTY_ARRAY } : {}),
    ...(row.voicemail_triage_template_id ? { SharedVoicemailTriageSettingsTemplateId: row.voicemail_triage_template_id } : {}),
  };
  // A design that leaves an extra unset keeps whatever Teams has: only a set one is compared.
  const extrasDiffer = (want: CallQueueActionSettings | null | undefined, have: { priority?: number; redirectPrompt?: string; redirectAudioId?: string } | undefined) => {
    const w = callQueueActionExtras('Overflow', want, audio);
    const tts = w.OverflowRedirectPersonTextToSpeechPrompt ?? w.OverflowRedirectVoicemailTextToSpeechPrompt;
    const file = (w.OverflowRedirectPersonAudioFilePrompt ?? w.OverflowRedirectVoicemailAudioFilePrompt) as string | undefined;
    return (
      (w.OverflowActionCallPriority !== undefined && w.OverflowActionCallPriority !== have?.priority) ||
      (tts !== undefined && tts !== have?.redirectPrompt) ||
      // a placeholder (not imported yet) never matches, so the import and Set happen
      (file !== undefined && file.toLowerCase() !== (have?.redirectAudioId ?? '').toLowerCase())
    );
  };

  if (!live) {
    // Every Microsoft Learn New-CsCallQueue example sets music on hold
    // explicitly (-UseDefaultMusicOnHold or -MusicOnHoldAudioFileId); no
    // custom hold music is modeled here, so use Teams' default.
    calls.push({ cmdlet: 'New-CsCallQueue', parameters: { ...parameters, UseDefaultMusicOnHold: true }, objectType: 'call_queue', objectId: row.id });
  } else {
    const sortedUsers = users.map((u) => u.toLowerCase()).sort();
    const sortedLive = (live.agentObjectIds ?? []).map((u) => u.toLowerCase()).sort();
    const changed =
      live.routingMethod !== row.routing_method ||
      live.agentAlertTime !== row.agent_alert_time ||
      live.presenceBasedRouting !== row.presence_based_routing ||
      (live.overflowAction ?? undefined) !== (row.overflow?.action ?? undefined) ||
      (live.overflowThreshold ?? undefined) !== (row.overflow?.threshold ?? undefined) ||
      !sameTarget(live.overflowActionTarget, overflowTarget) ||
      (live.timeoutAction ?? undefined) !== (row.timeout?.action ?? undefined) ||
      (live.timeoutThreshold ?? undefined) !== timeoutThreshold ||
      !sameTarget(live.timeoutActionTarget, timeoutTarget) ||
      (live.noAgentAction ?? undefined) !== (row.no_agent_action?.action ?? undefined) ||
      !sameTarget(live.noAgentActionTarget, noAgentTarget) ||
      (live.noAgentApplyTo ?? undefined) !== (row.no_agent_apply_to ?? undefined) ||
      extrasDiffer(row.overflow, live.overflowExtras) ||
      extrasDiffer(row.timeout, live.timeoutExtras) ||
      extrasDiffer(row.no_agent_action, live.noAgentExtras) ||
      (row.authorized_users != null && !sameIds(resolveUpnIds(row.authorized_users, agentObjectIds), live.authorizedUsers)) ||
      (row.hide_authorized_users != null && !sameIds(resolveUpnIds(row.hide_authorized_users, agentObjectIds), live.hideAuthorizedUsers)) ||
      (!!row.voicemail_triage_template_id && row.voicemail_triage_template_id.toLowerCase() !== (live.voicemailTriageTemplateId ?? '').toLowerCase()) ||
      (needsLanguage && (live.languageId ?? undefined) !== (row.language_id ?? undefined)) ||
      JSON.stringify(sortedUsers) !== JSON.stringify(sortedLive);
    if (changed) {
      calls.push({
        cmdlet: 'Set-CsCallQueue',
        parameters: { Identity: live.identity, ...parameters },
        objectType: 'call_queue',
        objectId: row.id,
      });
    }
  }

  // The queue needs a live Identity before it can be associated with a
  // resource account, so this only ever plans once New-CsCallQueue has
  // already run on a prior pass (mirrors the two-phase resource-account
  // pattern above). Diffed against live.applicationInstanceIds - same
  // pattern as planAutoAttendantRow's equivalent step - so it only emits
  // instance ids not already associated live, instead of re-emitting every
  // pass regardless.
  const linkedInstanceIds = row.resource_accounts.map((id) => raObjectIds.get(id)).filter((id): id is string => !!id);
  // Every still-linked resource account resolving is what makes the removal
  // diff below trustworthy - see the comment there.
  const allLinkedResolved = linkedInstanceIds.length === row.resource_accounts.length;
  const alreadyAssociated = new Set((live?.applicationInstanceIds ?? []).map((id) => id.toLowerCase()));
  const unassociatedInstanceIds = linkedInstanceIds.filter((id) => !alreadyAssociated.has(id.toLowerCase()));
  if (live?.identity && unassociatedInstanceIds.length > 0) {
    calls.push({
      cmdlet: 'New-CsOnlineApplicationInstanceAssociation',
      parameters: { Identities: unassociatedInstanceIds, ConfigurationId: live.identity, ConfigurationType: 'CallQueue' },
      objectType: 'call_queue',
      objectId: row.id,
    });
  }
  // A resource account unlinked in Design & Build keeps its live
  // association forever otherwise (confirmed bug: the diff above only ever
  // adds). Only emitted when every still-linked resource account resolved
  // this pass - if one didn't (not yet licensed, sync lag), skip removal
  // entirely rather than risk tearing down a still-wanted association based
  // on incomplete data; callQueueRowWarnings' existing unresolved-RA warning
  // already explains why nothing happened.
  if (allLinkedResolved) {
    const linkedInstanceIdSet = new Set(linkedInstanceIds.map((id) => id.toLowerCase()));
    const removedInstanceIds = (live?.applicationInstanceIds ?? []).filter((id) => !linkedInstanceIdSet.has(id.toLowerCase()));
    if (removedInstanceIds.length > 0) {
      calls.push({
        cmdlet: 'Remove-CsOnlineApplicationInstanceAssociation',
        parameters: { Identities: removedInstanceIds },
        objectType: 'call_queue',
        objectId: row.id,
      });
    }
  }

  return calls;
}

/* ========================================================================
 * Auto Attendants - structured call-flow config, mirroring
 * BuildAutoAttendantsTable's columns (packages/db/src/schema.ts). Reverse-
 * engineered against OVP012's real live Auto Attendants and Microsoft
 * Learn's New-CsAutoAttendant construction chain this session - see the
 * "reverse-engineer OVP012" plan. planAutoAttendantRow itself (the actual
 * cmdlet-chain builder) needs the `preamble` extension to CmdletInvocation
 * below and is implemented separately; these are the data shapes it will
 * consume, and what BuildAutoAttendantsTable/buildAutoAttendantWritable
 * already store today.
 * ======================================================================== */

/**
 * A transfer target - New-CsAutoAttendantCallableEntity's -Type, plus the
 * app-only 'auto_attendant'/'call_queue' kinds for same-site menu targets
 * (resolved to the target's live Identity at deploy time, the same way
 * planCallQueueRow resolves agent UPNs - see raObjectIds-style resolution).
 * Mirrors dto.ts's autoAttendantCallableEntitySchema.
 */
export interface AutoAttendantCallableEntity {
  kind: (typeof AA_CALLABLE_ENTITY_KINDS)[number];
  /** build_auto_attendants.id or build_call_queues.id - same-site, app-enforced like policy_ids. Only for kind 'auto_attendant'|'call_queue'. */
  buildId?: string;
  /**
   * The Teams id of an auto attendant / call queue that exists in the tenant but is not part of this site's design (so there is no
   * build row to point at). Kept as read from Teams so importing it loses nothing and deploying sends the same target. Only for
   * kind 'auto_attendant'|'call_queue'; ignored when `buildId` is set.
   */
  liveId?: string;
  /** UPN - only for kind 'user'. */
  upn?: string;
  /** tel: number or raw digits - only for kind 'external'. */
  number?: string;
  /** M365 group Object ID - only for kind 'shared_voicemail'. */
  groupId?: string;
}

/** New-CsAutoAttendantPrompt - a greeting or menu prompt. Mirrors dto.ts's autoAttendantPromptSchema. */
export interface AutoAttendantPrompt {
  type: (typeof GREETING_TYPES)[number];
  text?: string;
  /** files.id (an uploaded audio prompt) - only for type 'AudioFile'. */
  fileId?: string;
}

/** New-CsAutoAttendantMenuOption. Mirrors dto.ts's autoAttendantMenuOptionSchema. */
export interface AutoAttendantMenuOption {
  dtmf: (typeof AA_DTMF_RESPONSES)[number];
  action: (typeof AA_MENU_OPTION_ACTIONS)[number];
  target?: AutoAttendantCallableEntity;
  /** -Prompt - only for action 'Announcement' (played, then the caller returns to the menu). */
  prompt?: AutoAttendantPrompt;
}

/** New-CsAutoAttendantMenu. Mirrors dto.ts's autoAttendantMenuSchema. */
export interface AutoAttendantMenu {
  enableDialByName?: boolean;
  directorySearchMethod?: (typeof AA_DIRECTORY_SEARCH_METHODS)[number];
  options: AutoAttendantMenuOption[];
  /** -Prompts - the menu's own spoken prompt (e.g. "For Sales press 1, for Support press 2"), distinct from the CallFlow's Greetings (the initial "Thank you for calling..."). Confirmed against Microsoft Learn's own New-CsAutoAttendantMenu examples. */
  prompts?: AutoAttendantPrompt[];
}

/** New-CsAutoAttendantCallFlow. Mirrors dto.ts's autoAttendantCallFlowSchema. */
export interface AutoAttendantCallFlow {
  greetings: AutoAttendantPrompt[];
  menu: AutoAttendantMenu;
}

/** New-CsOnlineTimeRange. */
export interface AutoAttendantTimeRange {
  /** "HH:mm" */
  start: string;
  end: string;
}

/** New-CsOnlineSchedule. Mirrors dto.ts's autoAttendantScheduleSchema. */
export interface AutoAttendantSchedule {
  type: (typeof AA_SCHEDULE_TYPES)[number];
  weekly?: {
    monday: AutoAttendantTimeRange[];
    tuesday: AutoAttendantTimeRange[];
    wednesday: AutoAttendantTimeRange[];
    thursday: AutoAttendantTimeRange[];
    friday: AutoAttendantTimeRange[];
    saturday: AutoAttendantTimeRange[];
    sunday: AutoAttendantTimeRange[];
    /** New-CsOnlineSchedule -Complement - the hours above are business hours; this schedule fires outside them. */
    complement?: boolean;
  };
  fixed?: {
    /** ISO date strings, e.g. a holiday date range. */
    ranges: { start: string; end: string }[];
  };
}

/** One New-CsAutoAttendantCallHandlingAssociation of Type Holiday, paired with its own schedule. Mirrors dto.ts's autoAttendantHolidayCallFlowSchema. */
export interface AutoAttendantHolidayCallFlow {
  name: string;
  callFlow: AutoAttendantCallFlow;
  schedule: AutoAttendantSchedule;
}

export interface BuildAutoAttendantRow {
  id: string;
  name: string;
  language_id: string | null;
  time_zone_id: string | null;
  voice_id: string | null;
  voice_response_enabled: boolean;
  operator: AutoAttendantCallableEntity | null;
  default_call_flow: AutoAttendantCallFlow | null;
  after_hours_call_flow: AutoAttendantCallFlow | null;
  holiday_call_flows: AutoAttendantHolidayCallFlow[];
  schedule: AutoAttendantSchedule | null;
  /** build_resource_accounts.id array - resolved to Application Instance GUIDs via raObjectIds, same as BuildCallQueueRow's own field. */
  resource_accounts: string[];
  /** -AuthorizedUsers / -HideAuthorizedUsers as UPNs, and -UserNameExtension (voice-app-access.ts). null = not managed, Teams keeps its own. */
  authorized_users?: string[] | null;
  hide_authorized_users?: string[] | null;
  user_name_extension?: UserNameExtension | null;
}

/**
 * What Discovery's live-tenant snapshot (tenant_objects, object_type
 * 'auto_attendant') knows about an AA, matched by Name - same rationale as
 * CallQueueLiveState. `identity` is needed both to target Set-CsAutoAttendant
 * and to resolve this AA as a menu-option target from another AA (see
 * AutoAttendantCrossRef). `structured`, when the caller resolved it (the
 * deployment preview/execute path does; AutoAttendantCrossRef's own
 * identity-only resolution pass doesn't need to), is the same live object
 * converted to build_auto_attendants' own shape via
 * aa-live-parse.ts's liveAutoAttendantToStructured - what planAutoAttendantRow
 * diffs the saved row against to decide whether anything actually changed.
 */
export interface AutoAttendantLiveState {
  identity: string;
  languageId?: string;
  timeZoneId?: string;
  voiceId?: string;
  enableVoiceResponse?: boolean;
  structured?: LiveAutoAttendantStructured;
  /** Get-CsAutoAttendant's own ApplicationInstances array (the resource accounts already associated with it) - lets the resource-account association step diff instead of always re-sending. */
  applicationInstanceIds?: string[];
}

/**
 * Live Identity GUIDs for every same-site Auto Attendant/Call Queue, keyed
 * `${kind}:${buildId}` - resolves a menu option or -Operator that targets
 * another AA/CQ on the same site (AutoAttendantCallableEntity.buildId) to
 * the live GUID New-CsAutoAttendantCallableEntity -Type ApplicationEndpoint
 * -Identity needs. Built by the caller (mirrors raObjectIds/agentObjectIds
 * in planCallQueueRow) from every build_auto_attendants/build_call_queues
 * row on the site, not just the one(s) being planned - a target can point at
 * a sibling row outside the current plan/preview scope.
 */
export type AutoAttendantCrossRef = Map<string, string>;

function crossRefKey(kind: 'auto_attendant' | 'call_queue', buildId: string): string {
  return `${kind}:${buildId}`;
}

/**
 * Flags anything planAutoAttendantRow silently drops rather than guesses -
 * an unresolved menu-option/operator target, no default call flow at all
 * (nothing to deploy without one, so the row plans no calls), or a linked
 * resource account with no live Application Instance yet - same check,
 * same wording, as callQueueRowWarnings' own unresolved-RA warning (an AA
 * can have several resource accounts or none, exactly like a Call Queue).
 */
function collectUserUpnsFromCallFlow(cf: AutoAttendantCallFlow | null | undefined, out: string[]) {
  for (const opt of cf?.menu.options ?? []) {
    if (opt.action === 'TransferCallToTarget' && opt.target?.kind === 'user' && opt.target.upn) out.push(opt.target.upn);
  }
}
/**
 * Every UPN a 'user'-kind callable entity (an AA's -Operator, or a menu
 * option's TransferCallToTarget) references across a batch of rows -
 * planAutoAttendantRow/autoAttendantRowWarnings need each one resolved to
 * its live Entra Object ID first (see buildCallableEntity's 'user' case),
 * so the caller collects them up front the same way it already does for
 * Call Queue agent UPNs before calling resolveLiveIdentityState.
 */
export function collectAutoAttendantUserUpns(
  rows: Pick<BuildAutoAttendantRow, 'operator' | 'default_call_flow' | 'after_hours_call_flow' | 'holiday_call_flows' | 'authorized_users' | 'hide_authorized_users'>[],
): string[] {
  const out: string[] = [];
  for (const row of rows) {
    if (row.operator?.kind === 'user' && row.operator.upn) out.push(row.operator.upn);
    out.push(...(row.authorized_users ?? []), ...(row.hide_authorized_users ?? []));
    collectUserUpnsFromCallFlow(row.default_call_flow, out);
    collectUserUpnsFromCallFlow(row.after_hours_call_flow, out);
    for (const h of row.holiday_call_flows) collectUserUpnsFromCallFlow(h.callFlow, out);
  }
  return out;
}

/** One AA/CQ transfer target - see collectAutoAttendantVoiceAppRefs. */
export interface AutoAttendantVoiceAppRef {
  kind: 'auto_attendant' | 'call_queue';
  buildId: string;
}

function collectVoiceAppRefsFromCallFlow(cf: AutoAttendantCallFlow | null | undefined, out: AutoAttendantVoiceAppRef[]) {
  for (const opt of cf?.menu.options ?? []) {
    const target = opt.target;
    if (opt.action === 'TransferCallToTarget' && target && (target.kind === 'auto_attendant' || target.kind === 'call_queue') && target.buildId) {
      out.push({ kind: target.kind, buildId: target.buildId });
    }
  }
}

/**
 * Every same-site Auto Attendant/Call Queue a row's -Operator or menu
 * options transfer to (AutoAttendantCallableEntity.buildId) - used by
 * orderAutoAttendantRowsByDependency to sequence a deployment run so a
 * target deploys before the row that references it, the same way
 * collectAutoAttendantUserUpns collects 'user'-kind targets for identity
 * resolution.
 */
export function collectAutoAttendantVoiceAppRefs(
  row: Pick<BuildAutoAttendantRow, 'operator' | 'default_call_flow' | 'after_hours_call_flow' | 'holiday_call_flows'>,
): AutoAttendantVoiceAppRef[] {
  const out: AutoAttendantVoiceAppRef[] = [];
  if (row.operator && (row.operator.kind === 'auto_attendant' || row.operator.kind === 'call_queue') && row.operator.buildId) {
    out.push({ kind: row.operator.kind, buildId: row.operator.buildId });
  }
  collectVoiceAppRefsFromCallFlow(row.default_call_flow, out);
  collectVoiceAppRefsFromCallFlow(row.after_hours_call_flow, out);
  for (const h of row.holiday_call_flows) collectVoiceAppRefsFromCallFlow(h.callFlow, out);
  return out;
}

/**
 * Orders a batch of Auto Attendant rows being deployed together so a row
 * that transfers to another Auto Attendant IN THE SAME BATCH deploys after
 * its target - otherwise the target's live Identity may not exist yet when
 * this row is planned, and the transfer silently stays unresolved for the
 * rest of the run (flagged by autoAttendantRowWarnings, not fixed until a
 * later Discovery sync + a second deployment run). A Call Queue target isn't
 * ordered here - planCallQueueRow never references another build row, so
 * call_queues has nothing to topologically sort; the worker's block-level
 * order (call_queues before auto_attendants) already covers CQ-before-AA.
 * Stable for any row with no same-batch AA dependency (keeps the original
 * order); a same-batch cycle (A targets B, B targets A) can't be fully
 * ordered either way and is left as encountered, same as before this sort
 * existed.
 */
export function orderAutoAttendantRowsByDependency(rows: BuildAutoAttendantRow[]): BuildAutoAttendantRow[] {
  const dependsOn = new Map<string, Set<string>>();
  const idSet = new Set(rows.map((r) => r.id));
  for (const row of rows) {
    const deps = new Set<string>();
    for (const ref of collectAutoAttendantVoiceAppRefs(row)) {
      if (ref.kind === 'auto_attendant' && ref.buildId !== row.id && idSet.has(ref.buildId)) deps.add(ref.buildId);
    }
    dependsOn.set(row.id, deps);
  }
  const idToRow = new Map(rows.map((r) => [r.id, r]));
  const visited = new Set<string>();
  const visiting = new Set<string>();
  const ordered: BuildAutoAttendantRow[] = [];
  const visit = (id: string) => {
    if (visited.has(id) || visiting.has(id)) return; // already placed, or a cycle - leave the rest to unwind as-is
    visiting.add(id);
    for (const dep of dependsOn.get(id) ?? []) visit(dep);
    visiting.delete(id);
    visited.add(id);
    const row = idToRow.get(id);
    if (row) ordered.push(row);
  };
  for (const row of rows) visit(row.id);
  return ordered;
}

/**
 * Given the Call Queue/Auto Attendant rows selected for a deployment run and
 * this site's build_users rows, returns the build_users row ids that must
 * also be included so a referenced agent/-Operator/menu-option target's own
 * Enterprise Voice/number/policy config deploys before the queue/attendant
 * that relies on it, instead of the operator having to notice and select it
 * separately. Matching is by UPN, case-insensitive.
 */
export function collectDependencyUserRowIds(
  selectedCallQueues: Pick<BuildCallQueueRow, 'agents'>[],
  selectedAutoAttendants: Pick<BuildAutoAttendantRow, 'operator' | 'default_call_flow' | 'after_hours_call_flow' | 'holiday_call_flows'>[],
  siteUsers: { id: string; upn: string }[],
): string[] {
  const upns = new Set<string>();
  for (const cq of selectedCallQueues) for (const upn of cq.agents ?? []) upns.add(upn.toLowerCase());
  for (const upn of collectAutoAttendantUserUpns(selectedAutoAttendants)) upns.add(upn.toLowerCase());
  if (upns.size === 0) return [];
  return siteUsers.filter((u) => upns.has(u.upn.toLowerCase())).map((u) => u.id);
}

export function autoAttendantRowWarnings(
  row: BuildAutoAttendantRow,
  crossRef: AutoAttendantCrossRef,
  raObjectIds: Map<string, string>,
  userObjectIds: Map<string, string>,
): string[] {
  const warnings: string[] = [];
  const unresolvedRas = row.resource_accounts.filter((id) => !raObjectIds.has(id));
  if (unresolvedRas.length) {
    warnings.push(
      `${unresolvedRas.length} linked resource account(s) have no live Application Instance yet (New-CsOnlineApplicationInstance not run), so this Auto Attendant won't get a phone number until that's done.`,
    );
  }
  if (!row.default_call_flow) {
    warnings.push('No business-hours call flow configured yet, so nothing will deploy for this Auto Attendant.');
    return warnings;
  }
  if (!row.language_id || !row.time_zone_id) {
    warnings.push('Language and time zone must both be set before this Auto Attendant can be created - New-CsAutoAttendant requires both, and this row deploys nothing until they are.');
  }
  const unresolved: string[] = [];
  const check = (entity: AutoAttendantCallableEntity | undefined, where: string) => {
    if (!entity) return;
    if ((entity.kind === 'auto_attendant' || entity.kind === 'call_queue') && !entity.liveId && (!entity.buildId || !crossRef.has(crossRefKey(entity.kind, entity.buildId)))) {
      unresolved.push(`${where} targets an Auto Attendant/Call Queue that doesn't resolve live yet`);
    }
    if (entity.kind === 'user') {
      if (!entity.upn) unresolved.push(`${where} has no UPN set for its user target`);
      else if (!userObjectIds.has(entity.upn.toLowerCase())) unresolved.push(`${where} targets a user (${entity.upn}) that doesn't resolve to a live Entra identity yet`);
    }
    // Plain Voicemail has no deployable cmdlet form at all (Microsoft's own
    // -Type enum has no "Voicemail" entry) and buildCallableEntity always
    // returns undefined for it, falling back to DisconnectCall - warn every
    // time, there's no fixing this one from data. Shared Voicemail now
    // deploys correctly once groupId is set (see buildCallableEntity); only
    // warn while it's still missing.
    if (entity.kind === 'voicemail') {
      unresolved.push(`${where} targets plain Voicemail, which has no deployable cmdlet form - it will disconnect the call instead of deploying`);
    }
    if (entity.kind === 'shared_voicemail' && !entity.groupId) {
      unresolved.push(`${where} targets Shared Voicemail, but its M365 group ID isn't set yet - it will disconnect the call instead of deploying`);
    }
  };
  check(row.operator ?? undefined, 'Operator');
  const checkFlow = (cf: AutoAttendantCallFlow | null | undefined, label: string) => {
    for (const opt of cf?.menu.options ?? []) {
      if (opt.action === 'TransferCallToTarget') check(opt.target, `${label} DTMF ${opt.dtmf}`);
      // TransferCallToOperator routes via the AA's own -Operator (Microsoft
      // Learn: "the operator defined for the Auto attendant").
      if (opt.action === 'TransferCallToOperator' && !row.operator) {
        unresolved.push(`${label} DTMF ${opt.dtmf} transfers to the operator, but this Auto Attendant has no operator set`);
      }
      if (opt.action === 'Announcement' && !(opt.prompt?.type === 'Text' && opt.prompt.text)) {
        unresolved.push(`${label} DTMF ${opt.dtmf} is an announcement with no text - it will disconnect the call instead of deploying`);
      }
    }
  };
  checkFlow(row.default_call_flow, 'Business hours');
  checkFlow(row.after_hours_call_flow, 'After hours');
  for (const h of row.holiday_call_flows) checkFlow(h.callFlow, h.name);
  if (unresolved.length) warnings.push(...unresolved);
  // Same "after hours only deploys with both a flow and a schedule" rule as planAutoAttendantRow.
  if (row.after_hours_call_flow && row.schedule) warnings.push(...scheduleProblems(row.schedule, 'After-hours schedule'));
  for (const h of row.holiday_call_flows) warnings.push(...scheduleProblems(h.schedule, `Holiday "${h.name}"`));
  warnings.push(...accessWarnings(row.authorized_users, row.hide_authorized_users, userObjectIds));
  return warnings;
}

/** Accumulates one AA row's preamble steps plus its own per-prefix variable-name counters - passed through every builder below instead of module state, so nothing leaks across calls. */
interface AaBuildCtx {
  steps: PreambleStep[];
  counters: Record<string, number>;
}
function nextAaVar(ctx: AaBuildCtx, prefix: string): string {
  ctx.counters[prefix] = (ctx.counters[prefix] ?? 0) + 1;
  return `${prefix}${ctx.counters[prefix]}`;
}

/**
 * New-CsAutoAttendantCallableEntity - a menu option's transfer target or the
 * AA's -Operator. Returns undefined (skip, don't guess) when the target
 * can't resolve to a live identity - an unresolved same-site AA/CQ, or a
 * user target with no UPN. 'external'/'voicemail'/'shared_voicemail' always
 * resolve (a raw number needs no live lookup; Voicemail/SharedVoicemail
 * don't take an -Identity at all).
 */
function buildCallableEntity(ctx: AaBuildCtx, entity: AutoAttendantCallableEntity, crossRef: AutoAttendantCrossRef, userObjectIds: Map<string, string>): VarRef | undefined {
  let type: string;
  let identity: string | undefined;
  switch (entity.kind) {
    case 'auto_attendant':
    case 'call_queue':
      // Microsoft Learn's own -Type parameter doc is explicit: ApplicationEndpoint
      // is "when transferring to a Resource Account", ConfigurationEndpoint is
      // "when transferring directly to a nested Auto Attendant or Call Queue" -
      // this is the nested-AA/CQ case, not the resource-account one (that's the
      // separate New-CsOnlineApplicationInstanceAssociation step below).
      type = 'ConfigurationEndpoint';
      identity = entity.buildId ? crossRef.get(crossRefKey(entity.kind, entity.buildId)) : entity.liveId || undefined;
      if (!identity) return undefined;
      break;
    case 'user':
      // New-CsAutoAttendantCallableEntity -Type User needs the Enterprise-
      // Voice-enabled user's Entra Object ID (every Microsoft Learn example
      // resolves via Get-CsOnlineUser), not the raw UPN - mirrors how
      // planCallQueueRow already resolves agent UPNs via agentObjectIds.
      type = 'User';
      identity = entity.upn ? userObjectIds.get(entity.upn.toLowerCase()) : undefined;
      if (!identity) return undefined;
      break;
    case 'external':
      // New-CsAutoAttendantCallableEntity's -Identity for ExternalPstn is a
      // TEL URI (Microsoft Learn's own example: 'tel:+1234567890') - the
      // Design & Build editor only hints at the 'tel:' prefix via a
      // placeholder, it doesn't enforce it, so normalize here too.
      type = 'ExternalPstn';
      identity = entity.number ? normalizePstnTarget(entity.number) : undefined;
      if (!identity) return undefined;
      break;
    case 'voicemail':
      // Microsoft's cmdlet has no plain "Voicemail" -Type at all (Microsoft
      // Learn's -Type enum is only User | ApplicationEndpoint |
      // ConfigurationEndpoint | ExternalPstn | SharedVoicemail) and
      // -Identity is mandatory for every type it does support - there is no
      // way to render this kind correctly today. Skip it, same as any other
      // unresolved target (buildMenuOption falls back to DisconnectCall),
      // instead of sending an invalid enum value.
      return undefined;
    case 'shared_voicemail':
      // SharedVoicemail's -Identity is the M365 group's Object ID (Find-
      // CsGroup per Microsoft Learn's own example) - the Design & Build
      // editor now has a groupId field for it (a raw GUID; no Find-CsGroup
      // search yet, matching the bug's own accepted scope). Falls back to
      // the same "skip, don't guess" behavior as every other unresolved
      // target when it isn't set yet.
      type = 'SharedVoicemail';
      identity = entity.groupId;
      if (!identity) return undefined;
      break;
  }
  const varName = nextAaVar(ctx, 'ce');
  ctx.steps.push({ assignTo: varName, cmdlet: 'New-CsAutoAttendantCallableEntity', parameters: { Type: type, ...(identity ? { Identity: identity } : {}) } });
  return { $var: varName };
}

/**
 * New-CsAutoAttendantMenuOption. Per Microsoft Learn only TransferCallToTarget
 * takes a -CallTarget and only Announcement takes a -Prompt;
 * TransferCallToOperator and DisconnectCall take neither (operator routing
 * uses the AA's own -Operator). A TransferCallToTarget whose target didn't
 * resolve, or an Announcement with no text, falls back to DisconnectCall
 * rather than guess - autoAttendantRowWarnings surfaces both.
 */
function buildMenuOption(ctx: AaBuildCtx, opt: AutoAttendantMenuOption, crossRef: AutoAttendantCrossRef, userObjectIds: Map<string, string>): VarRef {
  let parameters: Record<string, unknown> = { Action: 'DisconnectCall', DtmfResponse: opt.dtmf };
  if (opt.action === 'TransferCallToTarget') {
    const callTarget = opt.target ? buildCallableEntity(ctx, opt.target, crossRef, userObjectIds) : undefined;
    if (callTarget) parameters = { Action: 'TransferCallToTarget', DtmfResponse: opt.dtmf, CallTarget: callTarget };
  } else if (opt.action === 'Announcement') {
    const promptRef = opt.prompt ? buildPrompt(ctx, opt.prompt) : undefined;
    if (promptRef) parameters = { Action: 'Announcement', DtmfResponse: opt.dtmf, Prompt: promptRef };
  } else if (opt.action === 'TransferCallToOperator') {
    parameters = { Action: 'TransferCallToOperator', DtmfResponse: opt.dtmf };
  }
  const varName = nextAaVar(ctx, 'opt');
  ctx.steps.push({ assignTo: varName, cmdlet: 'New-CsAutoAttendantMenuOption', parameters });
  return { $var: varName };
}

/**
 * New-CsAutoAttendantMenu itself rejects an empty menu ("must have either
 * menu options, dial-by-name or dial-by-extension") - confirmed live: this
 * is the real shape of a "just play the greeting" after-hours flow (no
 * caller interaction at all, e.g. "we're closed, please leave a
 * voicemail"). Microsoft's own documented way to model that is a single
 * option with DtmfResponse Automatic ("executed without user response"),
 * so the caller never sees a menu at all - synthesize one rather than
 * reject a row that has always been a legitimate, common AA shape. Shared
 * between buildMenu (what actually gets sent) and sortedMenuOptions (what a
 * saved row is compared against once live) so they never disagree - a
 * deployed row whose comparison didn't know about this synthesized option
 * saw a permanent, unfixable mismatch against its own live state, even
 * though nothing had actually changed - confirmed live right after this
 * exact fix first shipped.
 */
const SYNTHESIZED_DISCONNECT_OPTION: AutoAttendantMenuOption = { dtmf: 'Automatic', action: 'DisconnectCall' };
function effectiveMenuOptions(menu: AutoAttendantMenu): AutoAttendantMenuOption[] {
  return menu.options.length || menu.enableDialByName || menu.directorySearchMethod ? menu.options : [SYNTHESIZED_DISCONNECT_OPTION];
}

/** New-CsAutoAttendantPrompt. Only a text-to-speech prompt is deployable today - an AudioFile prompt needs Import-CsOnlineAudioFile run first (not modeled), so it's silently skipped rather than emitted broken. */
function buildPrompt(ctx: AaBuildCtx, p: AutoAttendantPrompt): VarRef | undefined {
  if (p.type !== 'Text' || !p.text) return undefined;
  const varName = nextAaVar(ctx, 'prompt');
  ctx.steps.push({ assignTo: varName, cmdlet: 'New-CsAutoAttendantPrompt', parameters: { TextToSpeechPrompt: p.text } });
  return { $var: varName };
}

/**
 * New-CsAutoAttendantMenu. `-Name` is mandatory (Microsoft Learn) - omitting
 * it doesn't error, it makes the cmdlet fall back to PowerShell's own
 * "Supply values for the following parameters: Name:" interactive prompt,
 * which then blocks forever since nothing is listening on stdin to answer
 * it. Confirmed live in production: this exact prompt, caught in a raw pwsh
 * session log, was the real cause behind three consecutive 15-minute
 * "hangs" that looked network- or module-related but had nothing to do with
 * either.
 */
function buildMenu(ctx: AaBuildCtx, menu: AutoAttendantMenu, name: string, crossRef: AutoAttendantCrossRef, userObjectIds: Map<string, string>): VarRef {
  const optionRefs = effectiveMenuOptions(menu).map((o) => buildMenuOption(ctx, o, crossRef, userObjectIds));
  const promptRefs = (menu.prompts ?? []).map((p) => buildPrompt(ctx, p)).filter((v): v is VarRef => !!v);
  const varName = nextAaVar(ctx, 'menu');
  ctx.steps.push({
    assignTo: varName,
    cmdlet: 'New-CsAutoAttendantMenu',
    parameters: {
      Name: `${name} Menu`,
      MenuOptions: optionRefs,
      ...(promptRefs.length ? { Prompts: promptRefs } : {}),
      ...(menu.enableDialByName ? { EnableDialByName: true } : {}),
      ...(menu.directorySearchMethod ? { DirectorySearchMethod: menu.directorySearchMethod } : {}),
    },
  });
  return { $var: varName };
}

/** New-CsAutoAttendantCallFlow. */
function buildCallFlow(ctx: AaBuildCtx, cf: AutoAttendantCallFlow, name: string, crossRef: AutoAttendantCrossRef, userObjectIds: Map<string, string>): VarRef {
  const greetingRefs = cf.greetings.map((g) => buildPrompt(ctx, g)).filter((v): v is VarRef => !!v);
  const menuRef = buildMenu(ctx, cf.menu, name, crossRef, userObjectIds);
  const varName = nextAaVar(ctx, 'flow');
  ctx.steps.push({
    assignTo: varName,
    cmdlet: 'New-CsAutoAttendantCallFlow',
    parameters: { Name: name, ...(greetingRefs.length ? { Greetings: greetingRefs } : {}), Menu: menuRef },
  });
  return { $var: varName };
}

const ISO_BOUND_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?)?(?:Z|[+-]\d{2}:?\d{2})?$/;
const DMY_BOUND_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?: (\d{1,2}):(\d{2}))?$/;

interface DateTimeBound {
  y: number;
  m: number;
  d: number;
  hh: number;
  mm: number;
  /** 'iso-date' = a bare YYYY-MM-DD (the AA wizard's date picker), whose END bound means "through that day" rather than midnight at its start. */
  form: 'iso-date' | 'iso-datetime' | 'dmy';
}

function parseDateTimeBound(raw: string): DateTimeBound | undefined {
  const v = raw.trim();
  let m = ISO_BOUND_RE.exec(v);
  if (m) {
    const timed = m[4] !== undefined;
    return { y: +m[1]!, m: +m[2]!, d: +m[3]!, hh: timed ? +m[4]! : 0, mm: timed ? +m[5]! : 0, form: timed ? 'iso-datetime' : 'iso-date' };
  }
  m = DMY_BOUND_RE.exec(v);
  if (m) return { y: +m[3]!, m: +m[2]!, d: +m[1]!, hh: m[4] !== undefined ? +m[4] : 0, mm: m[5] !== undefined ? +m[5] : 0, form: 'dmy' };
  return undefined;
}

function boundMs(b: DateTimeBound): number {
  return Date.UTC(b.y, b.m - 1, b.d, b.hh, b.mm);
}

function canonicalBound(ms: number): string {
  const t = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${t.getUTCFullYear()}-${p(t.getUTCMonth() + 1)}-${p(t.getUTCDate())}T${p(t.getUTCHours())}:${p(t.getUTCMinutes())}:00`;
}

/**
 * One holiday date range in the canonical stored form: ISO local date-times,
 * END EXCLUSIVE - the same shape Discovery reads back from a live
 * Get-CsOnlineSchedule (e.g. 2026-12-25T00:00:00 .. 2026-12-26T00:00:00 for
 * Christmas Day). Accepts what else gets saved: the AA wizard's bare
 * YYYY-MM-DD date-picker values (END inclusive - "To 25 Dec" means through
 * the 25th), an empty end (one day, matching New-CsOnlineDateTimeRange's own
 * default), and Microsoft's own d/m/yyyy [H:mm] form. Undefined = unparseable.
 */
export function normalizeFixedRange(r: { start: string; end: string }): { start: string; end: string } | undefined {
  const start = parseDateTimeBound(r.start ?? '');
  if (!start) return undefined;
  const startMs = boundMs(start);
  const DAY = 86_400_000;
  let endMs: number;
  if (!r.end?.trim()) {
    endMs = Date.UTC(start.y, start.m - 1, start.d) + DAY;
  } else {
    const end = parseDateTimeBound(r.end);
    if (!end) return undefined;
    endMs = end.form === 'iso-date' ? boundMs(end) + DAY : boundMs(end);
  }
  return { start: canonicalBound(startMs), end: canonicalBound(endMs) };
}

/** Canonical ISO bound -> New-CsOnlineDateTimeRange's only accepted input form, "d/m/yyyy H:mm" (Microsoft Learn). */
function renderDmyBound(canonical: string): string {
  const b = parseDateTimeBound(canonical)!;
  return `${b.d}/${b.m}/${b.y} ${b.hh}:${String(b.mm).padStart(2, '0')}`;
}

/** New-CsOnlineTimeRange takes a TimeSpan - an end-of-day bound must be 1.00:00 (Microsoft Learn's own 24-hour example), not 24:00, which TimeSpan can't parse. */
function renderTimeOfDay(hhmm: string): string {
  return hhmm === '24:00' ? '1.00:00' : hhmm;
}

function minutesOfDay(hhmm: string): number | undefined {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  return m ? +m[1]! * 60 + +m[2]! : undefined;
}

/** Everything about a schedule that would make New-CsOnlineTimeRange/-DateTimeRange/-Schedule reject it - surfaced as preview warnings instead of a failed run. */
function scheduleProblems(sched: AutoAttendantSchedule, where: string): string[] {
  const out: string[] = [];
  if (sched.type === 'fixed') {
    const ranges = sched.fixed?.ranges ?? [];
    ranges.forEach((r, i) => {
      const n = normalizeFixedRange(r);
      const label = ranges.length > 1 ? `${where} date range ${i + 1}` : `${where} dates`;
      if (!n) {
        out.push(`${label} aren't a recognisable date ("${r.start}" - "${r.end}").`);
        return;
      }
      if (n.end <= n.start) out.push(`${label} end on or before they start.`);
      if (!/:(00|30):00$/.test(n.start) || !/:(00|30):00$/.test(n.end)) out.push(`${label} must start and end on the hour or half hour.`);
    });
    return out;
  }
  const weekly = sched.weekly;
  const days = weekly ? WEEKLY_SCHEDULE_PARAMS.flatMap(([day]) => weekly[day].map((r) => ({ day, r }))) : [];
  if (days.length === 0) out.push(`${where} has no hours set on any day - Teams needs at least one.`);
  for (const { day, r } of days) {
    const s = minutesOfDay(r.start);
    const e = minutesOfDay(r.end);
    if (s === undefined || e === undefined || e <= s) out.push(`${where}: ${day} ${r.start}-${r.end} isn't a valid time range.`);
    else if (s % 15 || e % 15) out.push(`${where}: ${day} ${r.start}-${r.end} must use 15-minute steps.`);
  }
  return out;
}

/** Schedules compared after normalizing holiday ranges and -Complement, so a wizard-typed date and the live round-trip of the same range compare equal. */
function scheduleEqual(a: AutoAttendantSchedule | null, b: AutoAttendantSchedule | null): boolean {
  const canon = (s: AutoAttendantSchedule | null) => {
    if (!s) return null;
    if (s.type === 'fixed') {
      return { type: 'fixed', ranges: (s.fixed?.ranges ?? []).map((r) => normalizeFixedRange(r) ?? r).sort((x, y) => x.start.localeCompare(y.start)) };
    }
    const w = s.weekly;
    return { type: 'weekly', weekly: w ? { ...w, complement: !!w.complement } : undefined };
  };
  return structurallyEqual(canon(a), canon(b));
}

const WEEKLY_SCHEDULE_PARAMS = [
  ['monday', 'MondayHours'],
  ['tuesday', 'TuesdayHours'],
  ['wednesday', 'WednesdayHours'],
  ['thursday', 'ThursdayHours'],
  ['friday', 'FridayHours'],
  ['saturday', 'SaturdayHours'],
  ['sunday', 'SundayHours'],
] as const;

/** New-CsOnlineSchedule - -WeeklyRecurrentSchedule with per-day New-CsOnlineTimeRange objects, or -FixedSchedule with New-CsOnlineDateTimeRange objects (a distinct cmdlet from TimeRange - real dates, not times of day). */
function buildSchedule(ctx: AaBuildCtx, sched: AutoAttendantSchedule, name: string): VarRef {
  const varName = nextAaVar(ctx, 'sched');
  if (sched.type === 'fixed' && sched.fixed) {
    // An unparseable range is skipped rather than sent - scheduleProblems
    // already surfaces it as a preview warning.
    const rangeRefs = sched.fixed.ranges.flatMap((r) => {
      const n = normalizeFixedRange(r);
      if (!n) return [];
      const rv = nextAaVar(ctx, 'dtr');
      ctx.steps.push({ assignTo: rv, cmdlet: 'New-CsOnlineDateTimeRange', parameters: { Start: renderDmyBound(n.start), End: renderDmyBound(n.end) } });
      return [{ $var: rv }];
    });
    ctx.steps.push({ assignTo: varName, cmdlet: 'New-CsOnlineSchedule', parameters: { Name: name, FixedSchedule: true, DateTimeRanges: rangeRefs } });
  } else {
    const params: Record<string, unknown> = { Name: name, WeeklyRecurrentSchedule: true };
    for (const [day, param] of WEEKLY_SCHEDULE_PARAMS) {
      const ranges = sched.weekly?.[day] ?? [];
      if (ranges.length) {
        params[param] = ranges.map((r) => {
          const rv = nextAaVar(ctx, 'tr');
          ctx.steps.push({ assignTo: rv, cmdlet: 'New-CsOnlineTimeRange', parameters: { Start: renderTimeOfDay(r.start), End: renderTimeOfDay(r.end) } });
          return { $var: rv };
        });
      }
    }
    if (sched.weekly?.complement) params.Complement = true;
    ctx.steps.push({ assignTo: varName, cmdlet: 'New-CsOnlineSchedule', parameters: params });
  }
  return { $var: varName };
}

/** New-CsAutoAttendantCallHandlingAssociation - -ScheduleId/-CallFlowId are the *string* Id a locally-constructed schedule/call-flow object generates, not the object itself (see VarRef's `prop`). */
function buildCallHandlingAssociation(ctx: AaBuildCtx, type: 'AfterHours' | 'Holiday', scheduleRef: VarRef, callFlowRef: VarRef): VarRef {
  const varName = nextAaVar(ctx, 'cha');
  ctx.steps.push({
    assignTo: varName,
    cmdlet: 'New-CsAutoAttendantCallHandlingAssociation',
    parameters: { Type: type, ScheduleId: { ...scheduleRef, prop: 'Id' }, CallFlowId: { ...callFlowRef, prop: 'Id' } },
  });
  return { $var: varName };
}

/** Deep-equal with object keys sorted and `undefined` values dropped, so key order and an omitted-vs-undefined field never register as a difference. */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const v = (value as Record<string, unknown>)[key];
      if (v !== undefined) out[key] = canonicalize(v);
    }
    return out;
  }
  return value;
}
function structurallyEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(canonicalize(a)) === JSON.stringify(canonicalize(b));
}

/** Menu options compared by DTMF key rather than array position - a harmless reordering shouldn't read as a change. */
function sortedMenuOptions(cf: AutoAttendantCallFlow | null): AutoAttendantMenuOption[] {
  return cf ? [...effectiveMenuOptions(cf.menu)].sort((a, b) => a.dtmf.localeCompare(b.dtmf)) : [];
}
function callFlowEqual(a: AutoAttendantCallFlow | null, b: AutoAttendantCallFlow | null): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (!structurallyEqual(a.greetings, b.greetings)) return false;
  if (!structurallyEqual(a.menu.prompts ?? [], b.menu.prompts ?? [])) return false;
  if (!!a.menu.enableDialByName !== !!b.menu.enableDialByName) return false;
  if ((a.menu.directorySearchMethod ?? undefined) !== (b.menu.directorySearchMethod ?? undefined)) return false;
  return structurallyEqual(sortedMenuOptions(a), sortedMenuOptions(b));
}
/** A call flow's pieces in a few words each, for saying what differs between the design and the live one. */
function describeCallFlow(cf: AutoAttendantCallFlow | null): string {
  if (!cf) return 'none';
  const text = (v: unknown) => JSON.stringify(v).slice(0, 90);
  const option = (o: AutoAttendantMenuOption) => `${o.dtmf} ${o.action}${o.target ? ` to ${text(o.target)}` : ''}${o.prompt ? ` ${text(o.prompt)}` : ''}`;
  return [
    `greeting ${text(cf.greetings)}`,
    cf.menu.prompts?.length ? `menu prompt ${text(cf.menu.prompts)}` : null,
    cf.menu.enableDialByName ? 'dial by name' : null,
    cf.menu.directorySearchMethod ? `search ${cf.menu.directorySearchMethod}` : null,
    `options [${sortedMenuOptions(cf).map(option).join('; ')}]`,
  ].filter(Boolean).join(', ');
}

/** What differs between two call flows: the first piece that does, with both sides. Null when they are the same. */
function callFlowDifference(design: AutoAttendantCallFlow | null, live: AutoAttendantCallFlow | null): string | null {
  if (callFlowEqual(design, live)) return null;
  return `design: ${describeCallFlow(design)}; live: ${describeCallFlow(live)}`;
}

/** Holidays compared by name rather than array position, for the same reason. */
function sortedHolidays(list: AutoAttendantHolidayCallFlow[]): AutoAttendantHolidayCallFlow[] {
  return [...list].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * True when a saved row already matches what's live, field for field -
 * decides whether planAutoAttendantRow needs to send Set-CsAutoAttendant at
 * all. Compares against `live.structured` (aa-live-parse.ts's
 * liveAutoAttendantToStructured, run fresh by the caller against the same
 * live object) rather than the raw columns directly, because after-hours
 * only actually deploys when both the call flow AND its schedule are set
 * (see planAutoAttendantRow's own preamble-building below) - a half-filled
 * after-hours config that wouldn't be sent live yet must not register as a
 * "change" just because the column itself is non-null.
 */
function autoAttendantMatchesLive(row: BuildAutoAttendantRow, live: AutoAttendantLiveState, crossRef: AutoAttendantCrossRef, userObjectIds: Map<string, string>): boolean {
  return autoAttendantDifferences(row, live, crossRef, userObjectIds).length === 0;
}

/**
 * A transfer to another auto attendant / call queue is compared by the Teams id it reaches, however the design names it: a row of this
 * site (resolved through `crossRef`) and the id of one outside it are the same target when they are the same voice app. Without this a
 * target that became part of the site after the row was made would read as a change.
 */
function normalizeTarget(t: AutoAttendantCallableEntity | undefined, crossRef: AutoAttendantCrossRef): unknown {
  if (!t) return undefined;
  if (t.kind !== 'auto_attendant' && t.kind !== 'call_queue') return t;
  const id = t.buildId ? crossRef.get(crossRefKey(t.kind, t.buildId)) : t.liveId;
  return id ? { kind: 'voice_app', identity: id.toLowerCase() } : { kind: 'voice_app', unresolved: t.buildId ?? null };
}
function normalizeFlow(cf: AutoAttendantCallFlow | null, crossRef: AutoAttendantCrossRef): AutoAttendantCallFlow | null {
  if (!cf) return cf;
  return { ...cf, menu: { ...cf.menu, options: cf.menu.options.map((o) => (o.target ? { ...o, target: normalizeTarget(o.target, crossRef) as AutoAttendantCallableEntity } : o)) } };
}

/**
 * What differs between a saved row and the live auto attendant, in words, so the preview can say why a change is planned. Empty when
 * they match (or when nothing was read from the live one, in which case it says so: there is nothing to compare against).
 */
export function autoAttendantDifferences(
  row: BuildAutoAttendantRow,
  live: AutoAttendantLiveState,
  crossRef: AutoAttendantCrossRef = new Map(),
  userObjectIds: Map<string, string> = new Map(),
): string[] {
  const s = live.structured;
  if (!s) return ['the live settings could not be read'];
  const diffs: string[] = [];
  if (row.authorized_users != null && !sameIds(resolveUpnIds(row.authorized_users, userObjectIds), s.authorizedUsers)) diffs.push('authorised users');
  if (row.hide_authorized_users != null && !sameIds(resolveUpnIds(row.hide_authorized_users, userObjectIds), s.hideAuthorizedUsers)) diffs.push('hidden authorised users');
  if (row.user_name_extension && row.user_name_extension !== (s.userNameExtension ?? 'None')) diffs.push(`dial-by-name extension (design ${row.user_name_extension}, live ${s.userNameExtension ?? 'None'})`);
  if ((row.language_id ?? undefined) !== (s.languageId ?? undefined)) diffs.push(`language (design ${row.language_id ?? 'none'}, live ${s.languageId ?? 'none'})`);
  if ((row.time_zone_id ?? undefined) !== (s.timeZoneId ?? undefined)) diffs.push(`time zone (design ${row.time_zone_id ?? 'none'}, live ${s.timeZoneId ?? 'none'})`);
  if ((row.voice_id ?? undefined) !== (s.voiceId ?? undefined)) diffs.push(`voice (design ${row.voice_id ?? 'none'}, live ${s.voiceId ?? 'none'})`);
  if (!!row.voice_response_enabled !== !!s.enableVoiceResponse) diffs.push('voice response');
  if (!structurallyEqual(normalizeTarget(row.operator ?? undefined, crossRef) ?? null, normalizeTarget(s.operator ?? undefined, crossRef) ?? null)) diffs.push('operator');
  const bh = callFlowDifference(normalizeFlow(row.default_call_flow, crossRef), normalizeFlow(s.defaultCallFlow, crossRef));
  if (bh) diffs.push(`business hours call flow (${bh})`);

  const effectiveAfterHours = row.after_hours_call_flow && row.schedule ? row.after_hours_call_flow : null;
  const effectiveAfterHoursSchedule = row.after_hours_call_flow && row.schedule ? row.schedule : null;
  const ah = callFlowDifference(normalizeFlow(effectiveAfterHours, crossRef), normalizeFlow(s.afterHoursCallFlow, crossRef));
  if (ah) diffs.push(`after hours call flow (${ah})`);
  if (!scheduleEqual(effectiveAfterHoursSchedule, s.schedule)) diffs.push('after hours schedule');

  const rowHolidays = sortedHolidays(row.holiday_call_flows);
  const liveHolidays = sortedHolidays(s.holidayCallFlows);
  if (rowHolidays.length !== liveHolidays.length) diffs.push(`holidays (design ${rowHolidays.length}, live ${liveHolidays.length})`);
  else {
    for (let i = 0; i < rowHolidays.length; i++) {
      const a = rowHolidays[i]!;
      const b = liveHolidays[i]!;
      if (a.name !== b.name) diffs.push(`holiday name (design "${a.name}", live "${b.name}")`);
      else if (!callFlowEqual(normalizeFlow(a.callFlow, crossRef), normalizeFlow(b.callFlow, crossRef))) diffs.push(`holiday "${a.name}" call flow (${callFlowDifference(normalizeFlow(a.callFlow, crossRef), normalizeFlow(b.callFlow, crossRef))})`);
      else if (!scheduleEqual(a.schedule, b.schedule)) diffs.push(`holiday "${a.name}" dates`);
    }
  }
  return diffs;
}

/**
 * Turn a build_auto_attendants row into its cmdlet chain: the real
 * New-CsAutoAttendant construction chain (CallableEntity -> Prompt ->
 * MenuOption -> Menu -> CallFlow -> TimeRange/DateTimeRange -> Schedule ->
 * CallHandlingAssociation), run as one `preamble` before the single
 * mutating New-CsAutoAttendant/Set-CsAutoAttendant call, then a resource-
 * account association. Plans nothing when there's no default_call_flow yet -
 * see autoAttendantRowWarnings, which is what surfaces that (and any
 * unresolved target) to the preview.
 *
 * A live AA is only re-Set when autoAttendantMatchesLive finds an actual
 * difference - the preamble/parameters are only built at all in that case,
 * so a row that already matches the tenant plans nothing and disappears
 * from the preview, the same as every other object type. The resource-
 * account association is similarly only re-sent when `live`'s own
 * ApplicationInstances array (Get-CsAutoAttendant reliably exposes this,
 * confirmed against OVP012's real data - see the "front door" badge in the
 * call-flow diagram, which relies on the same field) doesn't already list
 * the target.
 */
export function planAutoAttendantRow(
  row: BuildAutoAttendantRow,
  crossRef: AutoAttendantCrossRef,
  raObjectIds: Map<string, string>,
  userObjectIds: Map<string, string>,
  live?: AutoAttendantLiveState,
): CmdletInvocation[] {
  const calls: CmdletInvocation[] = [];
  if (!row.default_call_flow) return calls;
  // New-CsAutoAttendant requires -LanguageId/-TimeZoneId (Microsoft Learn:
  // both Mandatory: True). Unlike the update path below (Set-CsAutoAttendant
  // via an existing $aa object), there's no live object to inherit a value
  // from when creating brand new, so a row missing either can't be created
  // at all - skip rather than send an incomplete mandatory-param call that
  // hangs on PowerShell's own interactive prompt (the same failure mode as
  // the already-fixed New-CsAutoAttendantMenu -Name bug). Surfaced to the
  // user ahead of time via autoAttendantRowWarnings.
  if (!live && (!row.language_id || !row.time_zone_id)) return calls;

  if (!live || !autoAttendantMatchesLive(row, live, crossRef, userObjectIds)) {
    const ctx: AaBuildCtx = { steps: [], counters: {} };

    const defaultFlowRef = buildCallFlow(ctx, row.default_call_flow, `${row.name} - Business hours`, crossRef, userObjectIds);
    const otherFlowRefs: VarRef[] = [];
    const chaRefs: VarRef[] = [];

    if (row.after_hours_call_flow && row.schedule) {
      const afRef = buildCallFlow(ctx, row.after_hours_call_flow, `${row.name} - After hours`, crossRef, userObjectIds);
      const schedRef = buildSchedule(ctx, row.schedule, `${row.name} - After hours schedule`);
      otherFlowRefs.push(afRef);
      chaRefs.push(buildCallHandlingAssociation(ctx, 'AfterHours', schedRef, afRef));
    }
    for (const holiday of row.holiday_call_flows) {
      const hfRef = buildCallFlow(ctx, holiday.callFlow, `${row.name} - ${holiday.name}`, crossRef, userObjectIds);
      const schedRef = buildSchedule(ctx, holiday.schedule, holiday.name);
      otherFlowRefs.push(hfRef);
      chaRefs.push(buildCallHandlingAssociation(ctx, 'Holiday', schedRef, hfRef));
    }

    const operatorRef = row.operator ? buildCallableEntity(ctx, row.operator, crossRef, userObjectIds) : undefined;

    if (!live) {
      // New-CsAutoAttendant takes every property directly as a parameter -
      // confirmed against Microsoft Learn.
      const parameters: Record<string, unknown> = {
        Name: row.name,
        ...(row.language_id ? { LanguageId: row.language_id } : {}),
        ...(row.time_zone_id ? { TimeZoneId: row.time_zone_id } : {}),
        ...(row.voice_id ? { VoiceId: row.voice_id } : {}),
        ...(row.voice_response_enabled ? { EnableVoiceResponse: true } : {}),
        DefaultCallFlow: defaultFlowRef,
        ...(otherFlowRefs.length ? { CallFlows: otherFlowRefs } : {}),
        ...(chaRefs.length ? { CallHandlingAssociations: chaRefs } : {}),
        ...(operatorRef ? { Operator: operatorRef } : {}),
        ...(row.authorized_users?.length ? { AuthorizedUsers: resolveUpnIds(row.authorized_users, userObjectIds) } : {}),
        ...(row.hide_authorized_users?.length ? { HideAuthorizedUsers: resolveUpnIds(row.hide_authorized_users, userObjectIds) } : {}),
        ...(row.user_name_extension ? { UserNameExtension: row.user_name_extension } : {}),
      };
      calls.push({ cmdlet: 'New-CsAutoAttendant', parameters, objectType: 'auto_attendant', objectId: row.id, preamble: ctx.steps });
    } else {
      // Unlike New-CsAutoAttendant, Set-CsAutoAttendant has no -Identity/
      // -Name/-LanguageId/... parameters at all - confirmed against
      // Microsoft Learn, its entire parameter surface is -Instance/-Tenant.
      // The only supported pattern is Get-CsAutoAttendant into a variable,
      // set each property on that object, then Set-CsAutoAttendant -Instance
      // $that - an earlier version of this function passed every parameter
      // directly the same way New-CsAutoAttendant does, which Teams would
      // have rejected outright the first time this ever actually ran live.
      ctx.steps.push({ assignTo: 'aa', cmdlet: 'Get-CsAutoAttendant', parameters: { Identity: live.identity } });
      const assign = (property: string, value: unknown) => ctx.steps.push({ kind: 'assign', target: 'aa', property, value });
      assign('Name', row.name);
      if (row.language_id) assign('LanguageId', row.language_id);
      if (row.time_zone_id) assign('TimeZoneId', row.time_zone_id);
      if (row.voice_id) assign('VoiceId', row.voice_id);
      // The object's property is VoiceResponseEnabled; EnableVoiceResponse is only
      // New-CsAutoAttendant's parameter name. Assigning the parameter name throws
      // (the property doesn't exist on the model), failing every update.
      assign('VoiceResponseEnabled', !!row.voice_response_enabled);
      assign('DefaultCallFlow', defaultFlowRef);
      assign('CallFlows', otherFlowRefs);
      assign('CallHandlingAssociations', chaRefs);
      // Removing the operator in Design & Build must clear it live too -
      // otherwise the live diff never converges. An operator that's set but
      // didn't resolve leaves the live one untouched (don't guess).
      if (operatorRef) assign('Operator', operatorRef);
      else if (!row.operator) assign('Operator', null);
      // PowerShell-only access settings (voice-app-access.ts): only a managed (non-null) list is set; an empty one clears it.
      if (row.authorized_users != null) assign('AuthorizedUsers', resolveUpnIds(row.authorized_users, userObjectIds).length ? resolveUpnIds(row.authorized_users, userObjectIds) : PS_EMPTY_ARRAY);
      if (row.hide_authorized_users != null) assign('HideAuthorizedUsers', resolveUpnIds(row.hide_authorized_users, userObjectIds).length ? resolveUpnIds(row.hide_authorized_users, userObjectIds) : PS_EMPTY_ARRAY);
      if (row.user_name_extension) assign('UserNameExtension', row.user_name_extension);
      calls.push({
        cmdlet: 'Set-CsAutoAttendant',
        parameters: { Instance: { $var: 'aa' } },
        objectType: 'auto_attendant',
        objectId: row.id,
        preamble: ctx.steps,
      });
    }
  }

  // Mirrors planCallQueueRow's own association-diff block exactly - only
  // emits instance ids not already associated live, instead of re-emitting
  // every pass regardless.
  const linkedInstanceIds = row.resource_accounts.map((id) => raObjectIds.get(id)).filter((id): id is string => !!id);
  // Every still-linked resource account resolving is what makes the removal
  // diff below trustworthy - see the comment there.
  const allLinkedResolved = linkedInstanceIds.length === row.resource_accounts.length;
  const alreadyAssociated = new Set((live?.applicationInstanceIds ?? []).map((id) => id.toLowerCase()));
  const unassociatedInstanceIds = linkedInstanceIds.filter((id) => !alreadyAssociated.has(id.toLowerCase()));
  if (live?.identity && unassociatedInstanceIds.length > 0) {
    calls.push({
      cmdlet: 'New-CsOnlineApplicationInstanceAssociation',
      parameters: { Identities: unassociatedInstanceIds, ConfigurationId: live.identity, ConfigurationType: 'AutoAttendant' },
      objectType: 'auto_attendant',
      objectId: row.id,
    });
  }
  // A resource account unlinked in Design & Build keeps its live
  // association forever otherwise (confirmed bug: the diff above only ever
  // adds). Only emitted when every still-linked resource account resolved
  // this pass - see planCallQueueRow's identical block for why.
  if (allLinkedResolved) {
    const linkedInstanceIdSet = new Set(linkedInstanceIds.map((id) => id.toLowerCase()));
    const removedInstanceIds = (live?.applicationInstanceIds ?? []).filter((id) => !linkedInstanceIdSet.has(id.toLowerCase()));
    if (removedInstanceIds.length > 0) {
      calls.push({
        cmdlet: 'Remove-CsOnlineApplicationInstanceAssociation',
        parameters: { Identities: removedInstanceIds },
        objectType: 'auto_attendant',
        objectId: row.id,
      });
    }
  }

  return calls;
}
